/* ==========================================================================
   bl_custom.js — shared BL behaviour
   ==========================================================================

   SCOPE:
   - Reusable custom elements for BL sections, built on the theme's own
     primitives (prev-next-buttons, page-dots, scrollable-content).
   - Section-specific one-offs do NOT belong here.

   Focal has no generic multi-item carousel: every stock carousel section
   (MultiColumn, ProductList, LogoList, GalleryList) reimplements the same
   prev-next listener against its own class names, and <native-carousel> is a
   one-at-a-time slideshow because NativeCarouselItem.selected is backed by the
   `hidden` attribute. bl-carousel fills that gap once.
   ========================================================================== */

/**
 * <bl-carousel> — a horizontal, multi-item scroller.
 *
 * Wires the theme's own <prev-next-buttons> and <page-dots> to a scroll
 * container, scrolling one page at a time. Scrolling itself is native CSS
 * overflow + scroll-snap; this only drives the controls and keeps their state
 * in sync, so with JS unavailable the track still scrolls by touch or trackpad.
 *
 * Expected markup:
 *   <bl-carousel>
 *     <div class="bl-carousel__viewport">   <-- the scrolling element
 *       ...items...
 *     </div>
 *     <prev-next-buttons>...</prev-next-buttons>   <-- optional
 *     <page-dots>...</page-dots>                   <-- optional
 *   </bl-carousel>
 *
 * Two stepping modes:
 *   default        pages by whole items that fit the viewport — a multi-item
 *                  scroller, as used by Top reviews.
 *   step="item"    advances one item at a time and centres it, for a carousel
 *                  where a single item is "current". The centred item gets the
 *                  `is-bl-active` class so CSS can highlight it, and dots track
 *                  items rather than pages.
 *
 * The viewport is found by [data-bl-carousel-viewport], falling back to the
 * first scrollable child. Page size is the viewport's own client width, matching
 * how the theme's stock sections page.
 *
 * Whether the track scrolls at all is left to CSS. A section that stacks its
 * items at narrow widths simply has no overflow there, so the controls disable
 * themselves and can be hidden with the theme's `hidden-phone` / `hidden-pocket`
 * utilities. That is how a carousel is kept desktop-only.
 */
class BlCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport =
      this.querySelector('[data-bl-carousel-viewport]') ||
      this.querySelector('.bl-carousel__viewport');

    if (!this.viewport) return;

    // Listeners live on this element and its own descendants, so they survive a
    // move. Binding again would stack them and step twice per click.
    if (this.initialised) {
      this._scheduleSync();
      return;
    }
    this.initialised = true;

    this.prevNextButtons = Array.from(this.querySelectorAll('prev-next-buttons'));
    this.pageDots = Array.from(this.querySelectorAll('page-dots'));

    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
    this._onDotChanged = this._onDotChanged.bind(this);
    this._syncControls = this._syncControls.bind(this);

    this.addEventListener('prev-next:prev', this._onPrev);
    this.addEventListener('prev-next:next', this._onNext);
    this.addEventListener('page-dots:changed', this._onDotChanged);

    this._buildLoop();
    this._onScrollIdle = this._onScrollIdle.bind(this);
    this.viewport.addEventListener('scroll', this._scheduleSync.bind(this), { passive: true });
    if (this.hasAttribute('center-on-play')) {
      this.viewport.addEventListener('click', this._onMediaClick.bind(this), true);
    }
    window.addEventListener('resize', this._scheduleSync.bind(this));

    // Re-measure whenever the track's box changes.
    //
    // This is what makes the controls survive the theme editor. Editing a
    // setting re-renders the section, and connectedCallback can run before the
    // new markup has been laid out — a zero-width viewport measures as "not
    // scrollable", which drops .is-scrollable (the CSS then hides the nav) and
    // disables prev. Nothing recovered it, because sync only re-ran on scroll
    // or window resize, and neither happens. The theme's own buttons looked
    // fine throughout because they just dispatch events and never measure.
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this._scheduleSync());
      this.resizeObserver.observe(this.viewport);
    }

    // Autoplay waits for the carousel to be on screen. Without this the first
    // video starts on page load however far down the page the section sits —
    // burning bandwidth on something nobody has scrolled to, and arriving
    // already part-played. Leaving view pauses again, for the same reason a
    // card being scrolled past stops.
    if (this.hasAttribute('autoplay-active') && window.IntersectionObserver) {
      this.inViewObserver = new IntersectionObserver(this._onInView.bind(this), { threshold: 0.35 });
      this.inViewObserver.observe(this);
    }

    // Let the theme editor reveal the block a merchant just selected.
    if (window.Shopify && window.Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        event.target.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: event.detail && event.detail.load ? 'auto' : 'smooth'
        });
      });
    }

    this._syncControls();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.inViewObserver) this.inViewObserver.disconnect();
    if (this._idleTimer) clearTimeout(this._idleTimer);
  }

  _onInView(entries) {
    const inView = entries.some((entry) => entry.isIntersecting);
    if (inView === this.inView) return;
    this.inView = inView;
    this._activeMediaItem = null;

    if (inView) {
      this._scheduleSync();
    } else {
      this._pendingPlayback = null;
      // -1 matches no card, so everything stops.
      this._stopMediaExcept(this.items, -1);
    }
  }

  /* The theme flips scroll maths for right-to-left storefronts. */
  get directionFlip() {
    const direction =
      window.themeVariables && window.themeVariables.settings
        ? window.themeVariables.settings.direction
        : 'ltr';
    return direction === 'ltr' ? 1 : -1;
  }

  get pageCount() {
    const pageSize = this.pageSize;
    if (pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(this.viewport.scrollWidth / pageSize));
  }

  get currentPage() {
    const pageSize = this.pageSize;
    if (pageSize <= 0) return 0;
    return Math.round((this.viewport.scrollLeft * this.directionFlip) / pageSize);
  }

  /* One item at a time, centred — rather than a page of items. */
  get stepsByItem() {
    return this.getAttribute('step') === 'item';
  }

  /**
   * Whether the CSS actually centres cards (scroll-snap-align: center). A
   * step="item" section can turn its track into a plain start-aligned
   * scroller at wider screens; centring "the card nearest the middle" there
   * moves the track half a card and proximity snap pulls it straight back, so
   * the arrows look dead. Read from the first card at the time of the step.
   */
  get centresItems() {
    const first = this.viewport.firstElementChild;
    if (!first) return false;
    return (getComputedStyle(first).scrollSnapAlign || '').indexOf('center') !== -1;
  }

  get items() {
    return Array.from(this.viewport.children);
  }

  /**
   * Endless track, opted into per section with `loop` — every other carousel on
   * the site keeps its hard ends. Under three items there is nothing to loop.
   */
  get loop() {
    return this.hasAttribute('loop') && this._realCount > 2;
  }

  /** Items the merchant actually authored, ignoring the copies either side. */
  get realCount() {
    return this._realCount || this.items.length;
  }

  /** Layout distance from one item to the next. */
  get itemStride() {
    const items = this.items;
    if (items.length < 2) return 0;
    // offsetLeft, not getBoundingClientRect: the rect is the PAINTED box, so a
    // card scaled down reports a left edge inset by however much it shrank and
    // the stride comes out too long. scrollLeft moves layout, so the stride has
    // to be measured in layout too.
    return Math.abs(items[1].offsetLeft - items[0].offsetLeft);
  }

  /**
   * Lays a copy of the whole set on each side, so the last card is followed by
   * the first and preceded by the last.
   *
   * A copied set rather than shuffled items is what makes this survive
   * scroll-snap: the reset below moves by exactly one set width, which is an
   * identical position on an identical card, so snap has nothing to correct.
   * Moving single items lands between snap points and the track fights back.
   *
   * A copy keeps its <video> but never plays one. The poster a reviewer's card
   * shows before playback is the video element's own poster attribute, so
   * removing the element left an empty white card on screen — the frame has to
   * come from somewhere. preload="none" means the copy costs no network: the
   * poster image paints, the media itself is never fetched, and autoplay and
   * loop are stripped so it cannot start on its own. _stopMediaExcept and
   * _playActiveMediaOnly skip copies as well, so a copy that is briefly centred
   * before the reset stays a still frame.
   *
   * Ids and focus are stripped too — duplicating ids would give the page
   * repeated ones and confuse block selection in the theme editor.
   */
  _buildLoop() {
    // Recorded before anything is copied and whether or not looping is on, so
    // realCount always means "what the merchant authored" — the loop getter
    // reads it, and would otherwise be asking a value it had not set yet.
    if (this._realCount === undefined) this._realCount = this.items.length;

    if (!this.hasAttribute('loop') || this._looped) return;
    if (this._realCount < 3) return;

    const originals = this.items;

    const copy = (item, side) => {
      const clone = item.cloneNode(true);
      clone.removeAttribute('id');
      clone.dataset.blClone = side;
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('video').forEach((video) => {
        video.removeAttribute('autoplay');
        video.removeAttribute('loop');
        video.removeAttribute('controls');
        video.preload = 'none';
        video.muted = true;
      });
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('a, button, [tabindex]').forEach((node) => {
        node.setAttribute('tabindex', '-1');
      });
      return clone;
    };

    const before = originals.map((item) => copy(item, 'before'));
    const after = originals.map((item) => copy(item, 'after'));

    after.forEach((clone) => this.viewport.append(clone));
    before.reverse().forEach((clone) => this.viewport.prepend(clone));

    this._looped = true;
  }

  /**
   * Silently puts the track back in the middle set.
   *
   * Called once scrolling has stopped, never during it — the jump is instant and
   * would otherwise cut a smooth scroll short. Because it moves a whole set
   * width onto the matching card, nothing appears to move.
   */
  _normaliseLoop() {
    if (!this.loop || !this._looped) return false;

    const stride = this.itemStride;
    if (!stride) return false;

    const setWidth = stride * this.realCount;
    const index = this.activeIndex;
    const direction = index < this.realCount ? 1 : index >= this.realCount * 2 ? -1 : 0;
    if (!direction) return false;

    // The copy that was centred has already grown to full size. Handing over to
    // the identical real card must not replay that growth, or the customer sees
    // the same video scale up twice in a row. Suppressed for this frame only,
    // so the swap is invisible and normal stepping keeps its animation.
    this.classList.add('is-bl-jumping');
    this.viewport.scrollLeft += setWidth * direction * this.directionFlip;
    return true;
  }

  /** Index of the item sitting closest to the middle of the viewport. */
  get activeIndex() {
    const items = this.items;
    if (!items.length) return 0;
    const box = this.viewport.getBoundingClientRect();
    const middle = box.left + box.width / 2;
    let best = 0;
    let bestDistance = Infinity;
    items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - middle);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  _onMediaClick(event) {
    if (!this.stepsByItem) return;
    const media = event.target.closest('bl-reel, external-video');
    if (!media) return;
    const items = this.items;
    const index = items.findIndex((item) => item.contains(media));
    if (index < 0) return;

    this._pendingPlayback = null;
    if (index === this.activeIndex && !items[index].dataset.blClone) return;

    event.preventDefault();
    event.stopPropagation();
    this._stopMediaExcept(items, -1);
    this._centreItem(index, true);
  }

  _centreItem(index, play = false) {
    const items = this.items;
    const targetIndex = Math.max(0, Math.min(index, items.length - 1));
    const target = items[targetIndex];
    this._pendingPlayback = play
      ? items[this.loop ? this.realCount + targetIndex % this.realCount : targetIndex]
      : null;
    if (target) {
      this._suspendSnap();
      target.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      this._scheduleSync();
    }
  }

  /**
   * Turns scroll snapping off for one programmatic scroll. Safari can refuse
   * or revert a smooth scrollIntoView/scrollBy inside a mandatory-snap track,
   * so it is done without snapping and the snapping is put back once the track
   * is idle (_onScrollIdle). The destination is a snap position itself, so
   * nothing moves when it comes back.
   */
  _suspendSnap() {
    if (this._snapSuspended) return;
    this._snapSuspended = true;
    this.viewport.style.scrollSnapType = 'none';
  }

  _restoreSnap() {
    if (!this._snapSuspended) return;
    this._snapSuspended = false;
    this.viewport.style.scrollSnapType = '';
  }

  _onPrev() {
    if (this.stepsByItem && this.centresItems) return this._centreItem(this.activeIndex - 1);
    this._scrollByPage(-1);
  }

  _onNext() {
    if (this.stepsByItem && this.centresItems) return this._centreItem(this.activeIndex + 1);
    this._scrollByPage(1);
  }

  _onDotChanged(event) {
    const index = event.detail ? event.detail.index : 0;
    // Always aim at the middle set, so there is room on both sides afterwards.
    if (this.loop && this.stepsByItem) return this._centreItem(this.realCount + index);
    if (this.stepsByItem) return this._centreItem(index);
    this.viewport.scrollTo({
      left: this.pageSize * index * this.directionFlip,
      behavior: 'smooth'
    });
  }

  /**
   * Distance of one page, rounded down to a whole number of items.
   *
   * Paging by the raw viewport width lands between scroll-snap points, and
   * `scroll-snap-type: mandatory` then drags the track to the nearest item edge
   * — so the position creeps and the arrows can disagree with what is on
   * screen. Measuring the item stride keeps every page on a snap point.
   */
  get pageSize() {
    const viewportWidth = this.viewport.clientWidth;
    const first = this.viewport.firstElementChild;
    if (!first) return viewportWidth;

    const gap = parseFloat(getComputedStyle(this.viewport).columnGap) || 0;
    const stride = first.getBoundingClientRect().width + gap;
    if (!stride || stride <= 0) return viewportWidth;

    const perPage = Math.max(1, Math.floor((viewportWidth + gap) / stride));
    return perPage * stride;
  }

  _scrollByPage(pages) {
    this._suspendSnap();
    this.viewport.scrollBy({
      left: this.pageSize * pages * this.directionFlip,
      behavior: 'smooth'
    });
    this._scheduleSync();
  }

  _scheduleSync() {
    if (this._syncFrame) cancelAnimationFrame(this._syncFrame);
    this._syncFrame = requestAnimationFrame(this._syncControls);

    if (!this.loop && !this._pendingPlayback && !this._snapSuspended) return;
    if (this._idleTimer) clearTimeout(this._idleTimer);
    this._idleTimer = setTimeout(this._onScrollIdle, 160);
  }

  /** The track has stopped: safe to jump back into the middle set. */
  _onScrollIdle() {
    this._idleTimer = null;
    this._restoreSnap();
    const jumped = this._normaliseLoop();
    const requestedItem = this._pendingPlayback;
    this._pendingPlayback = null;
    this._syncControls();

    const items = this.items;
    if (requestedItem && items[this.activeIndex] === requestedItem) {
      const externalVideo = requestedItem.querySelector('external-video');
      if (externalVideo && typeof externalVideo.play === 'function') {
        externalVideo.play().catch(() => {});
      } else {
        this._playActiveMediaOnly(items, this.activeIndex);
      }
    }

    if (jumped) {
      // Flush the swapped-in scale while transitions are still off, then let
      // them back on for the next real step.
      this.viewport.offsetHeight;
      this.classList.remove('is-bl-jumping');
    }
  }

  _syncControls() {
    // Nothing has been laid out yet. Measuring now would report "not
    // scrollable" and disable the controls; the ResizeObserver will call back
    // as soon as the track has a width.
    if (!this.viewport.clientWidth) return;

    // Open on the middle set so there is a full set to scroll into either way.
    // Deferred to here because it needs a laid-out stride, which connect lacks.
    if (this.loop && !this._centred) {
      const stride = this.itemStride;
      if (stride) {
        this.viewport.scrollLeft = stride * this.realCount * this.directionFlip;
        this._centred = true;
      }
    }

    const stepsByItem = this.stepsByItem;
    const items = this.items;
    const activeIndex = stepsByItem ? this.activeIndex : -1;

    const pageSize = this.viewport.clientWidth;
    const scrollLeft = this.viewport.scrollLeft * this.directionFlip;
    const maxScroll = this.viewport.scrollWidth - pageSize;
    // Sub-pixel layout means scrollLeft rarely lands exactly on maxScroll.
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft >= maxScroll - 1;
    const scrollable = maxScroll > 1;

    this.classList.toggle('is-scrollable', scrollable);

    if (stepsByItem) {
      items.forEach((item, index) => item.classList.toggle('is-bl-active', index === activeIndex));
      // Leaving a card always stops its video. Autoplay only decides whether
      // the card being arrived at starts playing, never whether the one being
      // left keeps going — otherwise a video carries on out of view, audible.
      this._stopMediaExcept(items, activeIndex);
      // Only once the section has been scrolled to — see the observer above.
      if (this.hasAttribute('autoplay-active') && this.inView && !this._pendingPlayback &&
          (!this.hasAttribute('center-on-play') || this._activeMediaItem !== items[activeIndex])) {
        this._warmMedia(items, activeIndex);
        this._playActiveMediaOnly(items, activeIndex);
      }
      this._activeMediaItem = items[activeIndex];
    }

    this.prevNextButtons.forEach((element) => {
      if (this.loop) {
        // No first or last any more, so neither arrow ever retires.
        element.isPrevDisabled = false;
        element.isNextDisabled = false;
      } else if (stepsByItem && this.centresItems) {
        element.isPrevDisabled = activeIndex <= 0;
        element.isNextDisabled = activeIndex >= items.length - 1;
      } else {
        element.isPrevDisabled = !scrollable || atStart;
        element.isNextDisabled = !scrollable || atEnd;
      }
    });

    // One dot per item when stepping by item; otherwise one per reachable page.
    // The copies either side are not pages of their own: one dot per authored
    // item, lit by where the centred card sits within its set. That is what
    // makes the counter reset invisible — passing from one set to the next
    // lands on the same dot.
    const dotCount = this.loop ? this.realCount : stepsByItem ? items.length : this.pageCount;
    let currentIndex = stepsByItem ? activeIndex : Math.min(this.currentPage, dotCount - 1);
    if (this.loop && stepsByItem) currentIndex = activeIndex % this.realCount;

    this.pageDots.forEach((element) => {
      // The snippet renders one dot per item because Liquid cannot know how
      // many fit per page — that depends on CSS at runtime. Hide the surplus so
      // the dot count always matches what is reachable.
      Array.from(element.querySelectorAll('button')).forEach((button, index) => {
        button.hidden = index >= dotCount;
      });

      element.selectedIndex = currentIndex;
    });
  }

  /**
   * With autoplay-active, only the centred item's video plays; the rest pause
   * and rewind. Advancing the carousel therefore hands playback to the next
   * one, rather than every video playing over the top of each other.
   */
  /** Pause and rewind every video except the one at activeIndex. */
  _stopMediaExcept(items, activeIndex) {
    items.forEach((item, index) => {
      if (index === activeIndex) return;
      if (this.hasAttribute('center-on-play')) {
        item.querySelectorAll('external-video').forEach((video) => {
          if (typeof video.pause === 'function') video.pause();
        });
      }
      const video = item.querySelector('video');
      if (!video || video.paused) return;
      video.pause();
      video.currentTime = 0;
      // Let the reel put its controls back into the paused state.
      item.querySelectorAll('bl-reel').forEach((reel) => {
        if (typeof reel.syncControls === 'function') reel.syncControls();
      });
    });
  }

  /**
   * Lets the centred card and the one after it buffer ahead of playback. Every
   * reel starts as preload="none" so a page full of videos costs nothing until
   * it is reached; once the viewer is here, the card they are on and the one
   * they will step to next are fetched, so playback starts without a stall and
   * the step does not open on a spinner. Copies in a loop are left alone.
   */
  _warmMedia(items, activeIndex) {
    [activeIndex, activeIndex + 1].forEach((index) => {
      const item = items[index];
      if (!item || item.dataset.blClone) return;
      const video = item.querySelector('video');
      if (video && video.preload !== 'auto') video.preload = 'auto';
    });
  }

  _playActiveMediaOnly(items, activeIndex) {
    const item = items[activeIndex];
    // A copy is only ever centred for the moment before the loop resets, and it
    // has no business playing: it would be a second copy of the same review
    // running alongside the real one.
    if (!item || item.dataset.blClone) return;
    const video = item.querySelector('video');
    // This runs on every scroll frame; a video already playing is left alone.
    if (!video || !video.paused) return;

    // A programmatic play needs muted to survive browser autoplay policy.
    video.muted = true;
    const played = video.play();
    if (played && typeof played.catch === 'function') played.catch(() => {});
  }
}

/**
 * <bl-reel> — a portrait video with its own always-visible controls.
 *
 * The browser's native control bar is not used. It can only sit at the bottom
 * of the video box, cannot be repositioned (the ::-webkit-media-controls
 * pseudos are non-standard and Chrome/Safari only), and it collided with the
 * name plate — which previously forced the video itself to be shortened. The
 * two controls the design shows are drawn here instead, so nothing shifts.
 *
 * Both controls are always present: autoplay decides what STARTS playing on
 * arrival, not whether the viewer can stop it. The section shows them on the
 * centred card only.
 *
 * The buttons are real <button>s, so they are keyboard reachable and labelled.
 * The rest of the frame is one big play/pause target, so only mute has to be
 * aimed at — see .bl-reel__hit in bl_custom.css.
 */
class BlReel extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    // connectedCallback runs again if the element is moved, so only set up once.
    if (!this.video || this.controls) return;

    // video_tag takes an image size, not an arbitrary poster, so a custom
    // poster is applied here instead.
    if (this.dataset.poster) this.video.poster = this.dataset.poster;

    // Nothing should be fetched until the viewer asks for it; the poster is
    // what they see until then.
    if (!this.video.hasAttribute('preload')) this.video.preload = 'none';

    this._chooseSource();

    // Always muted to start with, autoplay or not. A reel that makes noise the
    // moment it scrolls into view is the thing to avoid, and the mute button is
    // right there to turn sound on.
    this.video.muted = true;

    this.hitLayer = document.createElement('span');
    this.hitLayer.className = 'bl-reel__hit';
    this.hitLayer.setAttribute('aria-hidden', 'true');
    this.append(this.hitLayer);
    this.hitLayer.addEventListener('click', this._togglePlay.bind(this));

    this.controls = this.querySelector('[data-reel-controls]');
    if (this.controls) {
      this.playButton = this.controls.querySelector('[data-reel-play]');
      this.muteButton = this.controls.querySelector('[data-reel-mute]');

      if (this.playButton) this.playButton.addEventListener('click', this._togglePlay.bind(this));
      if (this.muteButton) this.muteButton.addEventListener('click', this._toggleMute.bind(this));
    }

    // The carousel drives playback too, so the buttons follow the video rather
    // than assuming their own clicks are the only thing that changes it.
    ['play', 'pause', 'ended', 'volumechange'].forEach((name) => {
      this.video.addEventListener(name, this.syncControls.bind(this));
    });

    this.syncControls();
  }

  /**
   * Keeps one MP4 rendition and drops the rest, so every browser plays the
   * same thing. Shopify's video_tag lists an HLS stream first and then MP4s
   * from the largest down, named by their height (HD-1080p-7.2Mbps,
   * HD-720p-4.5Mbps, SD-480p-1.5Mbps…). Left alone, Safari takes the HLS
   * stream — which starts low and steps up, so the first seconds look soft and
   * can stall at each step — and everything else takes the 1080p file, far
   * more than a card a few hundred pixels wide can show and slow to buffer.
   * The pick is the smallest rendition that still covers the card at the
   * screen's pixel density, capped at 720 lines, else the largest there is.
   * load() re-runs source selection; with preload="none" it fetches nothing.
   */
  _chooseSource() {
    const sources = Array.from(this.video.querySelectorAll('source'));
    if (sources.length < 2) return;

    // Shopify names each rendition "<name>.HD-1080p-7.2Mbps-<id>.mp4"; anything
    // else is not a rendition set, and the browser's own choice stands.
    const lines = (source) => {
      const match = (source.getAttribute('src') || '').match(/\.(?:HD|SD)-(\d{3,4})p-/);
      return match ? parseInt(match[1], 10) : 0;
    };
    const mp4s = sources
      .filter((source) => (source.getAttribute('type') || '').indexOf('mp4') !== -1 && lines(source) > 0)
      .sort((a, b) => lines(a) - lines(b));
    if (!mp4s.length) return;

    const target = Math.min(720, Math.ceil((this.clientWidth || 360) * (window.devicePixelRatio || 1)));
    const pick = mp4s.find((source) => lines(source) >= target) || mp4s[mp4s.length - 1];

    sources.forEach((source) => {
      if (source !== pick) source.remove();
    });
    this.video.load();
  }

  _togglePlay(event) {
    if (event) event.preventDefault();
    if (this.video.paused) {
      const played = this.video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
    } else {
      this.video.pause();
    }
  }

  _toggleMute(event) {
    if (event) event.preventDefault();
    this.video.muted = !this.video.muted;
    this.syncControls();
  }

  /** Put the buttons into the state the video is actually in. */
  syncControls() {
    if (!this.video) return;
    const playing = !this.video.paused && !this.video.ended;

    this.classList.toggle('is-playing', playing);
    this.classList.toggle('is-muted', this.video.muted);

    if (this.playButton) {
      this.playButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
      const label = this.playButton.querySelector('[data-reel-label]');
      if (label) label.textContent = this.playButton.dataset[playing ? 'labelPause' : 'labelPlay'] || '';
    }

    if (this.muteButton) {
      this.muteButton.setAttribute('aria-pressed', this.video.muted ? 'true' : 'false');
      const label = this.muteButton.querySelector('[data-reel-label]');
      if (label) label.textContent = this.muteButton.dataset[this.video.muted ? 'labelUnmute' : 'labelMute'] || '';
    }
  }
}

if (!window.customElements.get('bl-carousel')) {
  window.customElements.define('bl-carousel', BlCarousel);
}

if (!window.customElements.get('bl-reel')) {
  window.customElements.define('bl-reel', BlReel);
}

/**
 * <bl-variant-packs> — the "Buy X, Free Y" variant picker style.
 *
 * Each row of the selected pack chooses a variant. On any change the element
 * rewrites hidden items[] inputs, which the theme's own <product-form> then
 * posts to /cart/add.js — so the mini-cart refresh, loader button and error
 * handling are the theme's, not reimplemented here.
 *
 * The FULL pack quantity is added. Free items are removed by a Shopify
 * automatic discount configured on the store; nothing here discounts anything.
 *
 * Identical selections are summed into one line, so a cart reads
 * "Black x 4, Navy x 1" rather than six separate lines.
 */
class BlVariantPacks extends HTMLElement {
  connectedCallback() {
    if (this.inputsHost) return; // moved in the DOM; already wired

    this.inputsHost = this.querySelector('[data-pack-inputs]');
    if (!this.inputsHost) return;

    const data = this.querySelector('[data-pack-variants]');
    try {
      this.variants = data ? JSON.parse(data.textContent) : {};
    } catch (error) {
      this.variants = {};
    }

    this.addEventListener('change', this._onChange.bind(this));
    this._sync();
  }

  get form() {
    return document.getElementById(this.getAttribute('form-id'));
  }

  get packs() {
    return Array.from(this.querySelectorAll('[data-pack]'));
  }

  get selectedPack() {
    return this.packs.find((pack) => {
      const radio = pack.querySelector('[data-pack-radio]');
      return radio && radio.checked;
    }) || this.packs[0];
  }

  _onChange(event) {
    const option = event.target.matches('[data-pack-option]') ? event.target : null;
    const row = option && option.closest('[data-pack-row]');
    const pack = row && row.closest('[data-pack]');

    if (option && row && pack === this.selectedPack) {
      const rows = Array.from(pack.querySelectorAll('[data-pack-row]'));

      // The first item sets the same option for every item after it. Later
      // rows stay independent, so a customer can still mix colours or sizes.
      if (row === rows[0]) {
        const position = option.getAttribute('data-pack-option');

        rows.slice(1).forEach((otherRow) => {
          const matchingOption = otherRow.querySelector('[data-pack-option="' + position + '"]');
          if (matchingOption) matchingOption.value = option.value;
        });
      }
    }

    this._sync();

    if (row && pack === this.selectedPack) this._notifyMediaVariant(row);
  }

  /** Option values of one row, in the product's own option order. */
  _rowKey(row) {
    return Array.from(row.querySelectorAll('[data-pack-option]'))
      .map((select) => select.value)
      .join(' / ');
  }

  /**
   * Reuse Focal's own product-media filtering. Its product-media element
   * listens for the default picker's `variant:change` event and filters both
   * media and thumbnails from the image alt-tag groups.
   */
  _notifyMediaVariant(row) {
    const variant = this.variants[this._rowKey(row)];
    const form = this.form;
    if (!variant || !form) return;

    const previousVariant = this.mediaVariant || null;
    this.mediaVariant = variant;
    form.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: {
        formId: form.getAttribute('id'),
        variant: variant,
        previousVariant: previousVariant
      }
    }));
  }

  _sync() {
    const selected = this.selectedPack;
    if (!selected) return;

    this.packs.forEach((pack) => pack.classList.toggle('is-selected', pack === selected));

    // Colour swatches follow their select.
    selected.querySelectorAll('[data-pack-row]').forEach((row) => {
      const swatch = row.querySelector('[data-pack-swatch]');
      if (!swatch) return;
      const select = row.querySelector('[data-pack-option]');
      const option = select && select.selectedOptions[0];
      const colour = option && option.dataset.swatch;
      if (colour) swatch.style.background = colour;
    });

    this._writeInputs(selected);
    this._syncPrice(selected);
  }

  /**
   * Puts the selected pack's total into the add to cart button, into the
   * sticky bar's copy of it, and into the price under the product title.
   *
   * <bl-atc-price> (assets/custom.js) computes the button price from its own
   * "price" attribute plus every checked add-on, so writing the pack total to
   * that attribute is enough for the button to read "pack + add-ons" — the
   * add-on summing is untouched. <bl-installments> and <bl-product-price>
   * share that base, so they are written too; the pack's compare-at total
   * goes to the "compare" attribute for the struck price. None of them has
   * observedAttributes, so the change is announced with the same event the
   * add-on and bundle blocks use.
   *
   * The total is the one Liquid already rendered on the pack card, so the two
   * can never disagree. Nothing happens on a template without a packs block:
   * this element is not there to run.
   */
  _syncPrice(pack) {
    const formId = this.getAttribute('form-id');
    const total = pack.getAttribute('data-pack-total');
    const compare = pack.getAttribute('data-pack-compare');
    if (!formId || total === null) return;

    const prices = document.querySelectorAll('bl-atc-price[form-id="' + formId + '"], bl-installments[form-id="' + formId + '"], bl-product-price[form-id="' + formId + '"]');
    if (!prices.length) return;

    prices.forEach((price) => {
      price.setAttribute('price', total);
      if (compare !== null) price.setAttribute('compare', compare);
    });
    document.dispatchEvent(new CustomEvent('bl:selection:changed', { detail: { formId: formId } }));
  }

  _writeInputs(pack) {
    // Sum identical variants so the cart shows one line per variant.
    const counts = new Map();
    let unavailable = false;

    pack.querySelectorAll('[data-pack-row]').forEach((row) => {
      const variant = this.variants[this._rowKey(row)];
      if (!variant || !variant.available) {
        unavailable = true;
        row.classList.add('is-unavailable');
        return;
      }
      row.classList.remove('is-unavailable');
      counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
    });

    const formId = this.getAttribute('form-id');

    // Add-on analytics: the offer is tagged on every line the pack writes. In the cart quick-add sheet the
    // cross-sell tags (_ns_src and friends, see BlCartQuickAdd) ride along on each line too, because a
    // top-level properties[] field does not reach items[] lines.
    const offer = 'variant_pack:' + pack.getAttribute('data-quantity');
    const offerChanged = this.packs.indexOf(pack) === 0 ? '0' : '1';
    const tags = formId ? Array.from(document.querySelectorAll('input[data-ns-prop][form="' + formId + '"]')) : [];

    this.inputsHost.innerHTML = '';
    let index = 0;
    counts.forEach((quantity, id) => {
      this.inputsHost.append(
        this._hidden('items[' + index + '][id]', id, formId),
        this._hidden('items[' + index + '][quantity]', quantity, formId),
        this._hidden('items[' + index + '][properties][_ns_offer]', offer, formId),
        this._hidden('items[' + index + '][properties][_ns_offer_changed]', offerChanged, formId)
      );
      tags.forEach((tag) => {
        this.inputsHost.append(this._hidden('items[' + index + '][properties][' + tag.getAttribute('data-ns-prop') + ']', tag.value, formId));
      });
      index += 1;
    });

    // The theme posts a single `id` for a normal product. A pack sends items[]
    // instead, so that input must not go along with it.
    const single = this.form && this.form.querySelector('input[name="id"]');
    if (single) single.disabled = true;

    this.classList.toggle('has-unavailable', unavailable);
    const submit = this.form && this.form.querySelector('[data-product-add-to-cart-button]');
    if (submit) submit.disabled = unavailable || counts.size === 0;
  }

  _hidden(name, value, formId) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    if (formId) input.setAttribute('form', formId);
    return input;
  }
}

if (!window.customElements.get('bl-variant-packs')) {
  window.customElements.define('bl-variant-packs', BlVariantPacks);
}

/**
 * <bl-price-packs> — one variant sold in packs, each with its own price.
 *
 * The selected pack's quantity goes into the hidden `quantity` input the
 * theme's own <product-form> posts to /cart/add.js, so the mini-cart refresh,
 * loader button and error handling stay the theme's. Its price goes into the
 * add to cart button the same way <bl-variant-packs> does it.
 *
 * The pack price is display only: the cart charges the variant x quantity
 * until a Shopify automatic discount brings it down.
 *
 * A bundle checked in <bl-bundle-upsell> replaces the variant in the form, so
 * the quantity input is switched off while one is selected — otherwise the
 * pack quantity would multiply the bundle.
 */
class BlPricePacks extends HTMLElement {
  connectedCallback() {
    if (this.quantityInput) return; // moved in the DOM; already wired

    this.quantityInput = this.querySelector('[data-price-pack-quantity]');
    if (!this.quantityInput) return;

    this.addEventListener('change', this._onChange.bind(this));
    document.addEventListener('bl:selection:changed', this._syncBundle.bind(this));
    this._sync();
  }

  get selectedPack() {
    return this.querySelector('[data-price-pack]:checked');
  }

  _onChange(event) {
    if (event.target.matches('[data-price-pack]')) this._sync();
  }

  _sync() {
    const selected = this.selectedPack;
    if (!selected) return;

    this.quantityInput.value = selected.value;
    this._syncBundle();
    this._syncPrice(selected);
  }

  _syncBundle() {
    const formId = this.getAttribute('form-id');
    const bundle = document.querySelector('bl-bundle-upsell[form-id="' + formId + '"] [data-bl-bundle-option]:checked');
    this.quantityInput.disabled = Boolean(bundle && bundle.value !== 'main');
    this._writeOffer(bundle);
  }

  /**
   * Add-on analytics: keeps the hidden properties[_ns_offer] and properties[_ns_offer_changed] inputs in step.
   * A bundle swapped in by <bl-bundle-upsell> replaces the pack quantity, so it becomes the offer; otherwise
   * it is the selected pack, flagged as changed when it is not the pre-selected default.
   */
  _writeOffer(bundle) {
    const selected = this.selectedPack;
    const bundleHandle = bundle && bundle.value !== 'main' ? bundle.getAttribute('data-bundle-handle') : null;
    const offerInput = this.querySelector('input[data-ns-offer="offer"]');
    const changedInput = this.querySelector('input[data-ns-offer="changed"]');
    if (!offerInput || !changedInput || (!bundleHandle && !selected)) return;

    if (bundleHandle) {
      offerInput.value = 'bundle:' + bundleHandle;
      changedInput.value = '1';
    } else {
      offerInput.value = 'price_pack:' + selected.value;
      changedInput.value = selected.value === this.getAttribute('data-default-quantity') ? '0' : '1';
    }
  }

  /** Same contract as BlVariantPacks._syncPrice — see the notes there. */
  _syncPrice(pack) {
    const formId = this.getAttribute('form-id');
    const total = pack.getAttribute('data-price');
    const compare = pack.getAttribute('data-compare-price');
    if (!formId || total === null) return;

    const prices = document.querySelectorAll('bl-atc-price[form-id="' + formId + '"], bl-installments[form-id="' + formId + '"], bl-product-price[form-id="' + formId + '"]');
    if (!prices.length) return;

    prices.forEach((price) => {
      price.setAttribute('price', total);
      if (compare !== null) price.setAttribute('compare', compare);
    });
    document.dispatchEvent(new CustomEvent('bl:selection:changed', { detail: { formId: formId } }));
  }
}

if (!window.customElements.get('bl-price-packs')) {
  window.customElements.define('bl-price-packs', BlPricePacks);
}



/**
 * <bl-count-up> — counts a number up to its final value once it scrolls in.
 *
 * Only the [data-count-up-number] child is animated. Any unit around it —
 * "+", "%", "Day" — is a separate element and is left alone, because a unit
 * counting up reads as a glitch rather than as motion.
 *
 * The final value is what sits in the markup, so the real number is what shows
 * with JS off and to a crawler. The count overwrites it on scroll-in and puts
 * the authored string back on the last frame, so the design's own formatting
 * (thousands separators, decimals) survives however it was typed.
 *
 * Every element runs on its own as it scrolls in, so a row of them animates
 * together. There is deliberately no sequencing and no pre-zeroing: holding a
 * statistic at 0 while it waits its turn reads as broken data rather than as a
 * pending animation.
 *
 * Attributes:
 *   duration  ms for the count, default 1400.
 *   delay     ms to wait after scrolling in, default 0.
 */
class BlCountUp extends HTMLElement {
  connectedCallback() {
    this.number = this.querySelector('[data-count-up-number]');
    // connectedCallback runs again if the element is moved, so only set up once.
    if (!this.number || this.observer || this.started) return;

    this.finalText = this.number.textContent.trim();
    this.target = parseFloat(this.finalText.replace(/[^0-9.-]/g, ''));
    if (!isFinite(this.target)) return;

    // Trailing digits after the decimal point are kept for every frame, so
    // "4.8" counts 0.0 -> 4.8 rather than snapping between whole numbers.
    const dot = this.finalText.indexOf('.');
    this.decimals = dot === -1 ? 0 : this.finalText.length - dot - 1;
    this.grouped = this.finalText.indexOf(',') !== -1;

    // Nothing is animated for a viewer who asked for less motion, so the
    // markup's real number is left exactly as it is.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.observer = new IntersectionObserver(this._onIntersect.bind(this), { threshold: 0.4 });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.delayTimer) {
      clearTimeout(this.delayTimer);
      this.delayTimer = null;
    }
  }

  _onIntersect(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.disconnectedCallback();
    this.start();
  }

  start() {
    if (this.started) return;
    this.started = true;

    const delay = parseInt(this.getAttribute('delay'), 10) || 0;
    if (delay > 0) {
      this.delayTimer = setTimeout(this._run.bind(this), delay);
    } else {
      this._run();
    }
  }

  _run() {
    // Reserve the width the final string needs so the unit beside it cannot be
    // shoved sideways while the digits change.
    //
    // Measured HERE rather than in connectedCallback: at connect the web font
    // has usually not loaded, so that measures fallback-font text — wider than
    // Poppins — leaving an over-wide box that reads as a gap before the unit,
    // and on a narrow cell pushes the unit onto its own line. By scroll-in the
    // real font is in use.
    this.number.style.display = 'inline-block';
    this.number.style.minWidth = this.number.getBoundingClientRect().width + 'px';

    const duration = parseInt(this.getAttribute('duration'), 10) || 1400;
    const start = performance.now();

    const frame = (now) => {
      const elapsed = now - start;
      const progress = elapsed >= duration ? 1 : elapsed / duration;
      // easeOutCubic: fast first, settling onto the number rather than stopping dead.
      const eased = 1 - Math.pow(1 - progress, 3);

      if (progress === 1) {
        this._finish();
        return;
      }

      this.number.textContent = this._format(this.target * eased);
      requestAnimationFrame(frame);
    };

    requestAnimationFrame(frame);
  }

  _finish() {
    // Restore the authored string, so any formatting this cannot infer is
    // exactly what the viewer ends on.
    this.number.textContent = this.finalText;
    // Release the reservation: a px width measured at one breakpoint is wrong
    // at another, and once the count is over nothing needs it.
    this.number.style.minWidth = '';
    this.number.style.display = '';
    this.finished = true;
  }

  _format(value) {
    const fixed = value.toFixed(this.decimals);
    if (!this.grouped) return fixed;
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }
}

if (!window.customElements.get('bl-count-up')) {
  window.customElements.define('bl-count-up', BlCountUp);
}


/**
 * <bl-load-more> — reveals a list a few rows at a time.
 *
 * The section renders every item and limits the list in CSS, so a viewer with
 * no JS still gets the intended first rows rather than the lot. On upgrade this
 * element marks the list `is-js` — which turns that CSS limit off — and takes
 * over the hiding itself, so the count can change on click.
 *
 * Counts are per breakpoint because a row is a different number of items on
 * each: the section works them out from its own rows-per-breakpoint settings
 * and passes the totals in.
 *
 *   desktop-visible / mobile-visible   items shown initially
 *   desktop-step    / mobile-step      items added per click; 0 means all
 *   breakpoint                         px at which the desktop counts apply
 *
 * Expects [data-load-more-items] wrapping the items and, optionally,
 * [data-load-more-button] to trigger it. The button hides itself once
 * everything is out.
 */
class BlLoadMore extends HTMLElement {
  connectedCallback() {
    this.items = this.querySelector('[data-load-more-items]');
    // connectedCallback runs again if the element is moved, so only set up once.
    if (!this.items || this.entries) return;

    this.entries = Array.prototype.slice.call(this.items.children);
    this.button = this.querySelector('[data-load-more-button]');
    if (!this.entries.length) return;

    // Hand the limiting over from CSS to this element.
    this.items.classList.add('is-js');

    this.shown = this._initial();
    this._apply();

    if (this.button) this.button.addEventListener('click', this._onClick.bind(this));

    this._onResize = this._onResize.bind(this);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  _isWide() {
    const breakpoint = parseInt(this.getAttribute('breakpoint'), 10) || 1000;
    return window.matchMedia('(min-width: ' + breakpoint + 'px)').matches;
  }

  _count(name, fallback) {
    const value = parseInt(this.getAttribute(name), 10);
    return isNaN(value) ? fallback : value;
  }

  _initial() {
    const total = this.entries.length;
    return this._isWide() ? this._count('desktop-visible', total) : this._count('mobile-visible', total);
  }

  _step() {
    const step = this._isWide() ? this._count('desktop-step', 0) : this._count('mobile-step', 0);
    // 0 is "show the rest", which is also the sane fallback for a bad value.
    return step > 0 ? step : this.entries.length;
  }

  _onClick() {
    this.shown = Math.min(this.entries.length, this.shown + this._step());
    this._apply();
  }

  _onResize() {
    // A row is a different number of items per breakpoint, so the floor moves.
    // Anything already revealed stays revealed — collapsing it under someone
    // mid-read would be worse than showing a few extra.
    const initial = this._initial();
    if (this.shown < initial) {
      this.shown = initial;
      this._apply();
    }
  }

  _apply() {
    this.entries.forEach((entry, index) => {
      entry.classList.toggle('is-hidden', index >= this.shown);
    });

    if (this.button) {
      this.button.classList.toggle('is-hidden', this.shown >= this.entries.length);
    }
  }
}

if (!window.customElements.get('bl-load-more')) {
  window.customElements.define('bl-load-more', BlLoadMore);
}

class BlCollectionNavigation extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;
    this.addEventListener('click', this._onClick.bind(this));
  }

  _onClick(event) {
    const link = event.target.closest('a[data-bl-collection-filter]');
    if (!link || event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
        link.target && link.target !== '_self') return;

    const productFacet = document.querySelector('product-facet');
    if (!productFacet) return;

    const targetUrl = new URL(link.href, window.location.href);
    event.preventDefault();

    if (targetUrl.pathname === window.location.pathname && targetUrl.search === window.location.search) return;

    this.querySelectorAll('.bl-collection-tabs__link--active').forEach((activeLink) => {
      activeLink.classList.remove('bl-collection-tabs__link--active');
      activeLink.removeAttribute('aria-current');
    });
    link.classList.add('bl-collection-tabs__link--active');
    link.setAttribute('aria-current', 'page');

    productFacet.dispatchEvent(new CustomEvent('facet:criteria-changed', {
      bubbles: true,
      detail: { url: targetUrl.toString() }
    }));
  }
}

if (!window.customElements.get('bl-collection-navigation')) {
  window.customElements.define('bl-collection-navigation', BlCollectionNavigation);
}

/**
 * <bl-line-item-warranty> — the extended warranty offer under a cart line.
 *
 * Rendered by snippets/bl_line-item-warranty.liquid inside each qualifying
 * cart line. The toggle adds or removes the warranty product set in Theme
 * settings → Cart, and pairs the two lines with a shared WarrantyID line item
 * property (five random digits) so they can be matched later.
 *
 * Attributes, all written by the snippet from cart state:
 *   line-key, line-variant-id, line-quantity, line-selling-plan
 *                          the product line the offer belongs to
 *   line-properties        that line's properties as JSON, carried again in
 *                          full whenever the line is changed
 *   warranty-variant-id    the variant to add
 *   line-warranty-id       the WarrantyID already on the product line, if any
 *   warranty-line-key      the paired warranty line, while it is in the cart
 *   update-url             routes.cart_update_url
 *   enabled-by-default     add the warranty on first sight of an untagged line
 *
 * Every cart mutation costs the store a round trip of a second or more, so
 * each action makes the fewest it can. Turning the warranty on tags the
 * product line and adds the warranty line, in that order so that an
 * interruption (a tap on checkout, a dropped connection) leaves a tagged
 * product and no warranty, which costs the customer nothing and shows the
 * toggle off, never a warranty line nothing pairs with, which checkout would
 * charge. The drawer redraws from the HTML the add renders while the cart is
 * read back behind it for the header count (_refreshEarly), so the customer
 * waits for two requests, not three. Most lines never need any of this: a
 * product added from a form carrying bl_warranty-prepair arrives already
 * paired in the add itself. Turning the warranty off removes the warranty
 * line, one request; the product keeps its WarrantyID, and a tagged line
 * with no warranty is exactly what reads as declined, in this session and
 * the next. Every quantity change on the product line while its warranty is attached (Remove, minus,
 * plus, a typed quantity) is caught here and both lines go to the new
 * quantity in one update, their keys read back from the cart first
 * (_pairKeys), so one warranty unit always covers one product unit and
 * removing the product never leaves its warranty behind. Otherwise the cart
 * is only read when an attribute is missing.
 *
 * Loading follows the theme. In the drawer the bubbling
 * line-item-quantity:change:* events drive <line-item>'s own image loader, and
 * cart:refresh re-renders the drawer from the section HTML the last request
 * returns. On the cart page the theme's loading bar shows and the page reloads,
 * which is exactly what <line-item-quantity> does there.
 *
 * "Enable by default" adds the warranty the first time an untagged line is
 * seen. Turning it off is remembered per variant for as long as that product
 * stays in the cart (BlCartWarrantyGuard forgets it once the product is
 * gone), so a re-render does not put it straight back, while a later add of
 * the same product starts afresh. A line that already carries a
 * WarrantyID is never auto-tagged again, even when its warranty line is gone:
 * that is how a declined line looks, and how a warranty removed through its
 * own Remove link reads too.
 */
class BlLineItemWarranty extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');

    if (!this.input || this._bound) return;
    this._bound = true;

    this.input.addEventListener('change', this._onChange.bind(this));
    this._watchLineQuantity();

    if (this._shouldAutoAttach()) {
      this.input.checked = true;
      this._attach();
    }
  }

  get _routes() {
    return window.themeVariables.routes;
  }

  get _isCartPage() {
    return window.themeVariables.settings.pageType === 'cart';
  }

  get _quantity() {
    return parseInt(this.getAttribute('line-quantity'), 10) || 1;
  }

  get _warrantyVariantId() {
    return this.getAttribute('warranty-variant-id');
  }

  get _declineKey() {
    return 'bl-warranty-declined:' + this.getAttribute('line-variant-id');
  }

  _fetchCart() {
    return fetch(this._routes.cartUrl + '.js', { cache: 'reload' }).then((response) => response.json());
  }

  /**
   * The product line's own properties, which a change to the line must carry
   * again in full. They come from line-properties, where Liquid's json filter
   * writes the properties hash as an array of [name, value] pairs; the cart,
   * which gives an object, is only read when that is missing or unreadable,
   * or the line asked for is another one. Either way the set is cleaned of
   * what an earlier build of this element left behind, numbered properties
   * made from those pairs, so the next toggle repairs a line that has them.
   */
  async _lineProperties(key) {
    let properties = null;

    if (key === this.getAttribute('line-key') && this.hasAttribute('line-properties')) {
      try {
        properties = this._asProperties(JSON.parse(this.getAttribute('line-properties')));
      } catch (error) {
        // Unreadable: read the cart instead.
      }
    }

    if (!properties) {
      const cart = await this._fetchCart();
      const line = cart.items.find((item) => item.key === key);
      properties = this._asProperties(line ? line.properties : null) || {};
    }

    Object.keys(properties).forEach((name) => {
      if (/^\d+$/.test(name) && String(properties[name]).indexOf('WarrantyID') !== -1) delete properties[name];
    });

    return properties;
  }

  /**
   * A properties object from either shape, an object or an array of
   * [name, value] pairs. Anything else is null.
   */
  _asProperties(value) {
    if (Array.isArray(value)) {
      const properties = {};
      value.forEach((pair) => {
        if (Array.isArray(pair) && pair.length === 2 && typeof pair[0] === 'string') properties[pair[0]] = pair[1];
      });
      return properties;
    }

    return value && typeof value === 'object' ? Object.assign({}, value) : null;
  }

  /**
   * The key of the warranty line paired with this one, from warranty-line-key
   * or, failing that, from the cart.
   */
  async _warrantyLineKey(warrantyId) {
    if (this.hasAttribute('warranty-line-key')) return this.getAttribute('warranty-line-key');

    const cart = await this._fetchCart();
    const line = cart.items.find((item) => {
      return String(item.variant_id) === this._warrantyVariantId && item.properties && item.properties.WarrantyID === warrantyId;
    });
    return line ? line.key : null;
  }

  /**
   * Catches every quantity change on the product line while its warranty is
   * attached, so both lines can go in one request. The listener captures on
   * the line, above the theme's <line-item-quantity>, so the theme never sees
   * the event. In the drawer the line is the <line-item> around the offer; on
   * the cart page the offer has its own table row, right after the product's.
   */
  _watchLineQuantity() {
    if (!this.hasAttribute('warranty-line-key')) return;

    const line = this._lineElement();
    if (!line) return;

    line.addEventListener('click', this._onLineClick.bind(this), true);
    line.addEventListener('change', this._onLineChange.bind(this), true);
  }

  _lineElement() {
    const drawerLine = this.closest('line-item');
    if (drawerLine) return drawerLine;

    const row = this.closest('.bl-line-item-warranty-row');
    const previous = row ? row.previousElementSibling : null;
    return previous && previous.classList.contains('line-item') ? previous : null;
  }

  _onLineClick(event) {
    const link = event.target.closest('line-item-quantity a[href]');
    if (!link) return;

    const quantity = parseInt(new URL(link.href, window.location.origin).searchParams.get('quantity'), 10);
    if (Number.isNaN(quantity)) return;

    event.preventDefault();
    event.stopPropagation();
    this._changeWithWarranty(quantity);
  }

  _onLineChange(event) {
    if (!event.target.matches('line-item-quantity input')) return;

    event.stopPropagation();
    this._changeWithWarranty(blQuantityInputValue(event.target));
  }

  /**
   * Sets the product line and its warranty line to `quantity` in one request;
   * 0 removes both.
   */
  _changeWithWarranty(quantity) {
    return this._run(async () => {
      const keys = await this._pairKeys();

      // Nothing of the pair is in the cart any more: the cart as it stands is the state to show.
      if (!keys.length) return this._fetchCart();

      const updates = {};
      keys.forEach((key) => {
        updates[key] = quantity;
      });
      return this._post(this.getAttribute('update-url'), { updates: updates, sections: ['mini-cart'] });
    }, quantity);
  }

  /**
   * The keys of this line and of its warranty line as the cart holds them
   * now, matched by their shared WarrantyID rather than taken from the
   * attributes: a key the drawer rendered a moment ago may belong to a line
   * that has since changed, and /cart/update.js resolves a key whose hash no
   * longer matches to its variant and takes that variant's newest line.
   */
  async _pairKeys() {
    const cart = await this._fetchCart();
    const warrantyId = this.getAttribute('line-warranty-id');
    const lineVariantId = this.getAttribute('line-variant-id');

    return cart.items
      .filter((item) => {
        const variantId = String(item.variant_id);
        if (variantId !== lineVariantId && variantId !== this._warrantyVariantId) return false;
        return Boolean(item.properties) && item.properties.WarrantyID === warrantyId;
      })
      .map((item) => item.key);
  }

  _shouldAutoAttach() {
    if (!this.hasAttribute('enabled-by-default') || this.hasAttribute('line-warranty-id')) return false;
    // Never mutate the cart from inside the theme editor's preview.
    if (window.Shopify && window.Shopify.designMode) return false;

    try {
      return !window.sessionStorage.getItem(this._declineKey);
    } catch (error) {
      return true;
    }
  }

  _remember(declined) {
    try {
      if (declined) {
        window.sessionStorage.setItem(this._declineKey, '1');
      } else {
        window.sessionStorage.removeItem(this._declineKey);
      }
    } catch (error) {
      // Storage unavailable: the next render simply asks again.
    }
  }

  _onChange() {
    if (this.input.checked) {
      this._attach();
    } else {
      this._detach();
    }
  }

  async _post(url, body) {
    const response = await fetch(url + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    const json = await response.json();
    if (!response.ok) throw new Error(json.description || json.message || response.statusText);
    return json;
  }

  /**
   * Changes the product line's properties. Shopify replaces a line's
   * properties as a whole, so the full set is always sent.
   */
  _changeLine(key, properties, extra) {
    const body = Object.assign({ id: key, quantity: this._quantity, properties: properties }, extra);
    const sellingPlan = this.getAttribute('line-selling-plan');
    if (sellingPlan) body.selling_plan = parseInt(sellingPlan, 10);
    return this._post(this._routes.cartChangeUrl, body);
  }

  _attach() {
    const warrantyId = String(Math.floor(10000 + Math.random() * 90000));
    const lineKey = this.getAttribute('line-key');

    return this._run(async () => {
      const original = await this._lineProperties(lineKey);
      const properties = Object.assign({}, original, { WarrantyID: warrantyId });
      delete properties._bl_warranty;

      // The tag on the product line first, the warranty line second: interrupted in between, the cart holds
      // a tagged product and no warranty, which costs nothing and shows the toggle off, never a warranty
      // nothing pairs with. The add does not answer with the cart but does render the drawer, so the drawer
      // redraws from that at once and the cart is read back meanwhile for the header count and the
      // shipping bar.
      await this._changeLine(lineKey, properties);
      const added = await this._post(this._routes.cartAddUrl, {
        items: [{ id: parseInt(this._warrantyVariantId, 10), quantity: this._quantity, properties: { WarrantyID: warrantyId } }],
        sections: ['mini-cart']
      });

      this._refreshEarly(added.sections);
      this._remember(false);

      let cart = null;

      try {
        cart = await this._fetchCart();
        cart.sections = added.sections;
      } catch (error) {
        // The warranty is in the cart and the drawer has redrawn; only the header count waits for the next change.
      }

      return cart;
    });
  }

  /**
   * Redraws the drawer from section HTML a request already rendered, so the
   * customer does not wait for the cart read that follows. The theme's count
   * bubble reads item_count off the same event, so it is given the count it
   * shows plus what was just added; the real cart corrects it a moment later.
   */
  _refreshEarly(sections) {
    if (!sections || this._isCartPage) return;

    const bubble = document.querySelector('cart-count');
    const itemCount = (parseInt(bubble ? bubble.textContent : '', 10) || 0) + this._quantity;

    this._refreshedEarly = true;
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { cart: { sections: sections, item_count: itemCount }, replacementDelay: 250 }
    }));
  }

  _detach() {
    return this._run(async () => {
      const warrantyId = this.getAttribute('line-warranty-id');
      const warrantyKey = await this._warrantyLineKey(warrantyId);

      // The product line keeps its WarrantyID: tagged with no warranty line is what "declined" reads
      // as, on this render and on every later one, so one request is the whole change.
      const cart = warrantyKey
        ? await this._post(this._routes.cartChangeUrl, { id: warrantyKey, quantity: 0, sections: ['mini-cart'] })
        : await this._fetchCart();

      this._remember(true);
      return cart;
    });
  }

  async _run(work, quantity) {
    if (quantity === undefined) quantity = this._quantity;

    this.setAttribute('aria-busy', 'true');
    this.input.disabled = true;
    clearTimeout(this._inertTimer);
    this._setLineControlsInert(true);
    this.dispatchEvent(new CustomEvent('line-item-quantity:change:start', { bubbles: true, detail: { newLineQuantity: quantity } }));
    if (this._isCartPage) document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start'));

    try {
      const cart = await work();

      if (this._isCartPage) {
        window.location.reload();
        return;
      }

      this.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true, detail: { cart: cart || {}, newLineQuantity: quantity } }));
      if (cart && cart.items) document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));

      if (this._refreshedEarly) {
        // The drawer already redrew from the HTML the add rendered (_refreshEarly).
        this._refreshedEarly = false;
      } else if (cart && cart.sections) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, replacementDelay: 250 } }));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      this._refreshedEarly = false;
      // Put the switch back where it was; the re-render below shows real cart state.
      this.input.checked = !this.input.checked;
      this.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true }));

      if (this._isCartPage) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end'));
        this.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'error', error: error.message || '' }
        }));
      }

      this.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } finally {
      this.removeAttribute('aria-busy');
      this.input.disabled = false;
      // The controls stay out of reach until the re-render this work triggers has replaced them; the
      // delay only covers a re-render that never comes.
      this._inertTimer = setTimeout(() => this._setLineControlsInert(false), 1200);
    }
  }

  /**
   * Takes the line's quantity controls (+, −, the input and Remove) out of
   * reach while this element's work is in flight. The quantity input still
   * posts a line number, and while a warranty is being added the cart as
   * rendered is a request or two behind the cart as it stands, so a change
   * then lands on the warranty's own line, which the drawer does not show.
   * The links go by key and would only fail, but a tap mid-flight is better
   * refused than raced.
   */
  _setLineControlsInert(inert) {
    const line = this._lineElement();
    if (!line) return;

    line.querySelectorAll('line-item-quantity').forEach((controls) => {
      controls.inert = inert;
    });
  }
}

if (!window.customElements.get('bl-line-item-warranty')) {
  window.customElements.define('bl-line-item-warranty', BlLineItemWarranty);
}

/**
 * A quantity typed into a cart line's input, posted by the line's key.
 *
 * The theme's <line-item-quantity> posts the line's number (data-line) read
 * off the page. The extended warranty's lines are in the cart but not on the
 * page, and the page is a request or two behind the cart while a line is
 * changing, so a number can name a line other than the one shown: a hidden
 * warranty's, whose quantity then changes out of sight. Both carts give the
 * input its line's key in data-bl-key; this takes the change before the
 * theme does and posts the key instead, then loads and refreshes exactly as
 * the theme's element would. A line with its warranty attached is left to
 * <bl-line-item-warranty>, which moves both lines together. The plus, minus
 * and Remove links already carry the key in their href.
 */
function blQuantityInputValue(input) {
  // What the theme's <input is="input-number"> would settle on: empty falls back to the minimum, and
  // the value is kept within min and max.
  let quantity = parseInt(input.value, 10);
  if (Number.isNaN(quantity)) quantity = parseInt(input.min, 10) || 1;
  if (input.max !== '') quantity = Math.min(quantity, parseInt(input.max, 10));
  quantity = Math.max(quantity, parseInt(input.min, 10) || 0);
  input.value = String(quantity);
  return quantity;
}

function blLineHasAttachedWarranty(line) {
  if (!line) return false;
  if (line.querySelector('bl-line-item-warranty[warranty-line-key]')) return true;

  // On the cart page the offer sits in its own row, right after the product's.
  const next = line.nextElementSibling;
  return Boolean(next && next.classList.contains('bl-line-item-warranty-row') && next.querySelector('bl-line-item-warranty[warranty-line-key]'));
}

async function blChangeLineByKey(container, key, quantity) {
  const isCartPage = window.themeVariables.settings.pageType === 'cart';

  container.dispatchEvent(new CustomEvent('line-item-quantity:change:start', { bubbles: true, detail: { newLineQuantity: quantity } }));

  const response = await fetch(window.themeVariables.routes.cartChangeUrl + '.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: quantity, sections: ['mini-cart'] })
  });

  if (response.ok) {
    if (isCartPage) {
      window.location.reload();
      return;
    }

    const cart = await response.json();
    container.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true, detail: { cart: cart, newLineQuantity: quantity } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, replacementDelay: quantity === 0 ? 600 : 750 } }));
    return;
  }

  if (isCartPage) {
    const json = await response.json().catch(() => ({}));
    container.dispatchEvent(new CustomEvent('cart-notification:show', { bubbles: true, cancelable: true, detail: { status: 'error', error: json.description || '' } }));
  }

  container.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  container.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true }));
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('line-item-quantity input[data-bl-key]')) return;
  if (blLineHasAttachedWarranty(input.closest('line-item, .line-item'))) return;

  event.stopPropagation();

  const container = input.closest('line-item-quantity');
  blChangeLineByKey(container, input.getAttribute('data-bl-key'), blQuantityInputValue(input)).catch(() => {
    // Offline: show the cart as it stands.
    container.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true }));
    container.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  });
}, true);

/**
 * <bl-cart-warranty-guard> — keeps every warranty line paired with a product,
 * one unit per product unit.
 *
 * A warranty line is paired with its product by a shared WarrantyID property
 * (bl_line-item-warranty). Two things are repaired: an orphan — a warranty
 * line with no WarrantyID or one that no product line carries, because the
 * product left through a path that did not take the warranty with it, the
 * warranty reached the cart on its own, or the cart predates the pairing — is
 * removed; a paired warranty whose quantity differs from its product's is set
 * to the product's. Repairs go in one request.
 *
 * Rendered by snippets/bl_cart-warranty-guard.liquid inside both carts
 * whenever a warranty product is configured, so on every page. It acts:
 *
 *  - On load: Liquid works out the repairs for the cart it rendered and passes
 *    them in `orphan-keys` and `quantity-fixes`; they are confirmed against a
 *    fresh read and made in one request.
 *  - On every cart:updated on the document (the theme's product form, line
 *    quantities and cart note, and the BL warranty element all dispatch it),
 *    when the drawer opens, and when the page is shown again from the
 *    back-forward cache or after the tab was hidden: the cart is read back
 *    and swept, so a change that did not re-render the cart is caught too.
 *  - Before checkout: a submit of either cart's checkout form is held while
 *    the cart is read and repaired, for at most a few seconds, then released.
 *    Express checkout buttons bypass the form and get the other sweeps only.
 *
 * A warranty element's work is several requests, between which the cart may
 * be half changed, so a sweep waits while any warranty element on the page is
 * busy (aria-busy, set for the length of its work), and a repair found once
 * is read again after a short wait and only made if it is still needed.
 * Sweeps are debounced and never overlap.
 *
 * After a repair the carts refresh the way the theme does — cart:updated and
 * cart:refresh in the drawer, a reload on the cart page — and the refresh
 * renders the element again against the new cart, so the listeners are
 * dropped when an instance leaves the DOM. A sweep already in flight is left
 * to finish. A removal is remembered as done once Shopify has answered; a
 * repair that fails is retried with a backoff, and one that keeps failing is
 * left alone for the rest of the page.
 *
 * Attributes: update-url, warranty-product-id, orphan-keys and quantity-fixes
 * (both optional; quantity-fixes entries are "key|quantity").
 */
const blWarrantyRemovalsDone = new Set();
const blWarrantyRepairFailures = new Map();

class BlCartWarrantyGuard extends HTMLElement {
  connectedCallback() {
    // Never mutate the cart from inside the theme editor's preview.
    if (window.Shopify && window.Shopify.designMode) return;

    this._onCartUpdated = this._onCartUpdated || ((event) => this._scheduleSweep(event.detail && event.detail.cart));
    this._onPageShow = this._onPageShow || ((event) => { if (event.persisted) this._scheduleSweep(); });
    this._onVisibility = this._onVisibility || (() => { if (document.visibilityState === 'visible') this._scheduleSweep(); });
    this._onSubmit = this._onSubmit || this._beforeCheckout.bind(this);

    document.documentElement.addEventListener('cart:updated', this._onCartUpdated);
    window.addEventListener('pageshow', this._onPageShow);
    document.addEventListener('visibilitychange', this._onVisibility);
    document.addEventListener('submit', this._onSubmit, true);

    const drawer = document.querySelector('cart-drawer');
    if (drawer && 'MutationObserver' in window) {
      this._drawerObserver = new MutationObserver(() => { if (drawer.hasAttribute('open')) this._scheduleSweep(); });
      this._drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }

    const repairs = this._renderedRepairs();
    if (this._hasWork(repairs)) this._repair(repairs);
  }

  disconnectedCallback() {
    document.documentElement.removeEventListener('cart:updated', this._onCartUpdated);
    window.removeEventListener('pageshow', this._onPageShow);
    document.removeEventListener('visibilitychange', this._onVisibility);
    document.removeEventListener('submit', this._onSubmit, true);
    if (this._drawerObserver) this._drawerObserver.disconnect();
    clearTimeout(this._sweepTimer);
  }

  /**
   * The repairs Liquid worked out for the cart it rendered.
   */
  _renderedRepairs() {
    const read = (name) => {
      try {
        return JSON.parse(this.getAttribute(name) || '[]');
      } catch (error) {
        return [];
      }
    };

    const quantities = {};
    read('quantity-fixes').forEach((entry) => {
      const at = typeof entry === 'string' ? entry.lastIndexOf('|') : -1;
      if (at > 0) quantities[entry.slice(0, at)] = parseInt(entry.slice(at + 1), 10);
    });

    return { removals: read('orphan-keys').filter(Boolean), quantities: quantities };
  }

  /**
   * `cart`, when the event carried one, is what the sweep looks at first, so
   * a change with nothing to repair costs no request at all.
   */
  _scheduleSweep(cart) {
    this._knownCart = cart && Array.isArray(cart.items) ? cart : null;
    clearTimeout(this._sweepTimer);
    this._sweepTimer = setTimeout(() => this._sweep(), 400);
  }

  _fetchCart() {
    return fetch(window.themeVariables.routes.cartUrl + '.js', { cache: 'reload' }).then((response) => response.json());
  }

  /**
   * The repairs `cart` needs: warranty lines nothing pairs with, to remove,
   * and paired warranty lines whose quantity differs from their product's,
   * to set to it.
   */
  _repairsFor(cart) {
    const warrantyProductId = this.getAttribute('warranty-product-id');
    const repairs = { removals: [], quantities: {} };
    if (!warrantyProductId || !cart || !Array.isArray(cart.items)) return repairs;

    const products = new Map();
    cart.items.forEach((item) => {
      const id = item.properties && item.properties.WarrantyID;
      if (String(item.product_id) !== warrantyProductId && id) products.set(id, item);
    });

    cart.items
      .filter((item) => String(item.product_id) === warrantyProductId)
      .forEach((item) => {
        const id = item.properties && item.properties.WarrantyID;
        const product = id ? products.get(id) : null;

        if (!product) {
          repairs.removals.push(item.key);
        } else if (product.quantity !== item.quantity) {
          repairs.quantities[item.key] = product.quantity;
        }
      });

    return repairs;
  }

  _hasWork(repairs) {
    return repairs.removals.length > 0 || Object.keys(repairs.quantities).length > 0;
  }

  /**
   * A warranty turned off is remembered for its product only while that
   * product is in the cart. Once the product is gone, the memory goes with
   * it, so a later add of the same product gets the warranty by default again.
   */
  _forgetDeclinesFor(cart) {
    if (!cart || !Array.isArray(cart.items)) return;

    const prefix = 'bl-warranty-declined:';
    const variantsInCart = new Set(cart.items.map((item) => String(item.variant_id)));

    try {
      Object.keys(window.sessionStorage)
        .filter((key) => key.indexOf(prefix) === 0 && !variantsInCart.has(key.slice(prefix.length)))
        .forEach((key) => window.sessionStorage.removeItem(key));
    } catch (error) {
      // Storage unavailable: nothing was remembered in the first place.
    }
  }

  async _sweep() {
    if (this._sweeping) return;

    if (this._warrantyWorkInFlight()) {
      this._scheduleSweep();
      return;
    }

    this._sweeping = true;

    try {
      const known = this._knownCart;
      this._knownCart = null;
      const cart = known || await this._fetchCart();
      this._forgetDeclinesFor(cart);
      const repairs = this._repairsFor(cart);

      if (this._hasWork(repairs)) {
        // Confirm after a pause: a change in flight elsewhere may settle it meanwhile.
        await new Promise((resolve) => setTimeout(resolve, 600));
        await this._repair(repairs);
      }
    } catch (error) {
      this._retryLater();
    } finally {
      this._sweeping = false;
    }
  }

  /**
   * Holds a checkout submit while the cart is read and repaired, then lets it
   * go. Held for a few seconds at most, and always released.
   */
  _beforeCheckout(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.blWarrantyChecked || !form.querySelector('[name="checkout"]')) return;

    event.preventDefault();

    const release = () => {
      form.dataset.blWarrantyChecked = '1';
      form.submit();
    };
    const limit = new Promise((resolve) => setTimeout(resolve, 4000));

    Promise.race([this._sweepNow(), limit]).then(release, release);
  }

  /**
   * A sweep that neither waits for a quiet moment nor re-renders: used while
   * a checkout submit is held.
   */
  async _sweepNow() {
    const started = Date.now();
    while (this._warrantyWorkInFlight() && Date.now() - started < 3000) {
      await new Promise((resolve) => setTimeout(resolve, 150));
    }

    const repairs = this._repairsFor(await this._fetchCart());
    if (this._hasWork(repairs)) await this._repair(repairs, { silent: true });
  }

  /**
   * A warranty element's own work is in flight; the cart may be half changed
   * until it is done.
   */
  _warrantyWorkInFlight() {
    return Boolean(document.querySelector('bl-line-item-warranty[aria-busy="true"]'));
  }

  /**
   * Tries again after a failure, backing off from 2 s to 30 s so an offline
   * customer is not hammered.
   */
  _retryLater() {
    this._retryDelay = Math.min((this._retryDelay || 1000) * 2, 30000);
    clearTimeout(this._sweepTimer);
    this._sweepTimer = setTimeout(() => this._sweep(), this._retryDelay);
  }

  _mayTry(id) {
    return !blWarrantyRemovalsDone.has(id) && (blWarrantyRepairFailures.get(id) || 0) < 5;
  }

  /**
   * Makes `repairs` in one request, after a fresh read has confirmed that
   * each one is still needed: a key read off the page may be stale, and
   * /cart/update.js resolves a key whose hash no longer matches to its
   * variant and takes that variant's newest line. A repair that fails
   * (offline, throttled) is retried with a backoff; a removal is remembered
   * as done only once Shopify has answered, and any repair is left alone for
   * the rest of the page after five failures. With `silent` the cart is not
   * re-rendered afterwards.
   */
  async _repair(repairs, options) {
    const silent = Boolean(options && options.silent);
    let removals = repairs.removals.filter((key) => key && this._mayTry(key + '|0'));
    let quantities = Object.keys(repairs.quantities).filter((key) => this._mayTry(key + '|' + repairs.quantities[key]));
    if (!removals.length && !quantities.length) return;

    if (!silent && this._warrantyWorkInFlight()) {
      this._scheduleSweep();
      return;
    }

    let current;

    try {
      current = this._repairsFor(await this._fetchCart());
    } catch (error) {
      if (!silent) this._retryLater();
      return;
    }

    removals = removals.filter((key) => current.removals.indexOf(key) !== -1);
    quantities = quantities.filter((key) => current.quantities[key] === repairs.quantities[key]);
    if (!removals.length && !quantities.length) return;

    const updates = {};
    const attempted = [];
    removals.forEach((key) => {
      updates[key] = 0;
      attempted.push(key + '|0');
    });
    quantities.forEach((key) => {
      updates[key] = repairs.quantities[key];
      attempted.push(key + '|' + repairs.quantities[key]);
    });

    let response = null;

    try {
      response = await fetch(this.getAttribute('update-url') + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ updates: updates, sections: ['mini-cart'] })
      });
    } catch (error) {
      // Offline: handled below.
    }

    if (!response || !response.ok) {
      attempted.forEach((id) => blWarrantyRepairFailures.set(id, (blWarrantyRepairFailures.get(id) || 0) + 1));
      if (!silent) this._retryLater();
      return;
    }

    removals.forEach((key) => blWarrantyRemovalsDone.add(key + '|0'));
    this._retryDelay = 0;

    if (silent) return;

    if (window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
      return;
    }

    let cart = null;

    try {
      cart = await response.json();
    } catch (error) {
      // The repair went through but its answer did not: re-render from a fresh fetch instead.
    }

    if (cart) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }
}

if (!window.customElements.get('bl-cart-warranty-guard')) {
  window.customElements.define('bl-cart-warranty-guard', BlCartWarrantyGuard);
}

/**
 * <bl-cart-recommendations> — the cross-sell block in the cart drawer.
 *
 * Stands in for the theme's <cart-drawer-recommendations> in
 * sections/mini-cart.liquid. That element fetches /recommendations/products
 * with no intent, so it can only ever return "related" products; this one
 * passes `intent` (complementary unless told otherwise), so the block follows
 * the Complementary products set per product in Search & Discovery. Where that
 * render comes back empty — none configured, or every one filtered out by
 * Liquid — `fallback-intent` (related, in the mini-cart) is fetched instead.
 *
 * The response is the mini-cart section rendered with the recommendations
 * object, and Liquid there drops the warranty product and products hidden from
 * search (seo.hidden). Products already in the cart are dropped here instead,
 * from `cart-products` (the ids in the cart at this render), and the list is
 * cut to `max-items`. So one render per product and intent serves every state
 * of the cart, cached for the life of the page: a quantity change costs no
 * request and shows no spinner, only a new product does. Both intents are
 * fetched at once, so the fallback never costs a second round trip; each is
 * judged by what it has left to show once the cart's products are dropped.
 *
 * Attributes: section-id, product-id, intent (default complementary),
 * fallback-intent, limit (default 10), max-items (default 6), cart-products.
 */
const blCartRecommendationsCache = {};

class BlCartRecommendations extends HTMLElement {
  async connectedCallback() {
    const productId = this.getAttribute('product-id');
    const sectionId = this.getAttribute('section-id');

    if (!productId || !sectionId) {
      this.hidden = true;
      return;
    }

    const limit = parseInt(this.getAttribute('limit'), 10) || 10;
    const maxItems = parseInt(this.getAttribute('max-items'), 10) || 6;
    const inCart = new Set((this.getAttribute('cart-products') || '').split(',').filter(Boolean));
    const intents = [this.getAttribute('intent') || 'complementary'];
    const fallbackIntent = this.getAttribute('fallback-intent');

    if (fallbackIntent && !intents.includes(fallbackIntent)) {
      intents.push(fallbackIntent);
    }

    const renders = await Promise.all(intents.map((intent) => this._fetch(productId, sectionId, intent, limit)));

    // The drawer re-renders on every cart change; this element may be gone by now.
    if (!this.isConnected) return;

    for (const fetched of renders) {
      if (fetched && this._select(fetched, inCart, maxItems) > 0) {
        this.innerHTML = fetched.innerHTML;
        return;
      }
    }

    this.hidden = true;
  }

  /**
   * Drops the cards of products already in the cart, and any past max-items,
   * from a fetched render, and returns how many are left. The render is parsed
   * fresh from the cached text each time, so the cache itself is never touched.
   */
  _select(fetched, inCart, maxItems) {
    let kept = 0;

    fetched.querySelectorAll('[data-bl-cart-rec-list] > [data-product-id]').forEach((card) => {
      if (inCart.has(card.getAttribute('data-product-id')) || kept >= maxItems) {
        card.remove();
      } else {
        kept += 1;
      }
    });

    return kept;
  }

  /**
   * Resolves to the <bl-cart-recommendations> element of the section rendered
   * for one intent, or null when the request failed. A failed request is
   * dropped from the cache so the next render tries it again.
   */
  async _fetch(productId, sectionId, intent, limit) {
    const cacheKey = [productId, intent, limit].join('|');

    if (!blCartRecommendationsCache[cacheKey]) {
      blCartRecommendationsCache[cacheKey] = fetch(
        `${window.themeVariables.routes.productRecommendationsUrl}?product_id=${productId}&limit=${limit}&section_id=${sectionId}&intent=${intent}`
      ).then((response) => response.text());
    }

    let html;

    try {
      html = await blCartRecommendationsCache[cacheKey];
    } catch (error) {
      delete blCartRecommendationsCache[cacheKey];
      return null;
    }

    const div = document.createElement('div');
    div.innerHTML = html;

    return div.querySelector('bl-cart-recommendations');
  }
}

if (!window.customElements.get('bl-cart-recommendations')) {
  window.customElements.define('bl-cart-recommendations', BlCartRecommendations);
}

/**
 * ------------------------------------------------------------------------------------------------
 * <bl-loyalty> — the write path for the Appstle loyalty page
 * ------------------------------------------------------------------------------------------------
 *
 * Everything a loyalty section READS is server-rendered from Shopify metafields, so this element
 * only handles the actions. It talks to Appstle's documented Storefront API through the Shopify app
 * proxy, which signs each request and passes logged_in_customer_id — so no API key ever reaches the
 * page. The proxy prefix comes from Appstle's own config when their embed is present.
 *
 * One element wraps the whole page; every button inside declares itself with data-loyalty-action.
 *
 *   claim  data-earn-rule-id   POST /api/claim-social-media-points
 *   redeem data-redeem-rule-id POST /api/redeem-points
 *   copy   data-copy           clipboard, no network
 *
 * A 400 from claim means "already claimed" — Appstle enforces one claim per rule, so a double click
 * is safe and the button simply settles into its claimed state.
 */
class BlLoyalty extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.addEventListener('click', this._onClick.bind(this));
    this._armCountUp();
  }

  /**
   * Counts the balance up from zero the first time it scrolls into view.
   *
   * The number is server-rendered, so it is already correct before any of this runs — the animation
   * only replaces it temporarily and always lands back on the exact string Liquid produced, which
   * keeps whatever formatting the section chose. Skipped entirely under reduced motion, where the
   * rendered value simply stays put.
   */
  _armCountUp() {
    const nodes = this.querySelectorAll('[data-loyalty-balance]');
    if (!nodes.length) return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    nodes.forEach((node) => {
      const final = node.textContent.trim();
      const target = parseInt(final.replace(/[^0-9]/g, ''), 10);
      if (!target || target < 0) return;

      // Group digits mid-flight only if the rendered value already does, so the width does not jump.
      const grouped = /[.,\s]/.test(final);
      // The real figure, kept aside so a redeem mid-animation can still do arithmetic on it.
      node._blPoints = target;
      node.textContent = grouped ? (0).toLocaleString() : '0';

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          // A redeem that lands before the reveal must win: the balance is already correct by then.
          if (node._blCountDone) return;
          this._countTo(node, target, final, grouped);
        });
      }, { threshold: 0.4 });

      observer.observe(node);
    });
  }

  _countTo(node, target, final, grouped) {
    const DURATION = 1100;
    const started = performance.now();

    const frame = (now) => {
      const progress = Math.min(1, (now - started) / DURATION);

      if (progress < 1) {
        // Ease-out cubic — quick off the mark, settling gently onto the real figure.
        const value = Math.round(target * (1 - Math.pow(1 - progress, 3)));
        node.textContent = grouped ? value.toLocaleString() : String(value);
        node._blCountRaf = requestAnimationFrame(frame);
      } else {
        node.textContent = final;
        node._blCountRaf = null;
        node._blCountDone = true;
      }
    };

    node._blCountRaf = requestAnimationFrame(frame);
  }

  get base() {
    const prefix = (window._ALConfig && window._ALConfig.proxy_path_prefix) || 'apps/loyalty';
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}${prefix}/cp/`;
  }

  get customerId() {
    return this.getAttribute('customer-id') || '';
  }

  async _post(path, body) {
    const response = await fetch(this.base + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }
    return { ok: response.ok, status: response.status, payload };
  }

  _onClick(event) {
    const anchor = event.target.closest('a[href^="#"]');
    if (anchor && this._scrollToAnchor(anchor)) {
      event.preventDefault();
      return;
    }

    const trigger = event.target.closest('[data-loyalty-action]');
    if (!trigger || trigger.disabled) return;

    const action = trigger.getAttribute('data-loyalty-action');
    if (action === 'copy') {
      event.preventDefault();
      this._copy(trigger);
    } else if (action === 'claim') {
      event.preventDefault();
      this._claim(trigger);
    } else if (action === 'redeem') {
      event.preventDefault();
      this._redeem(trigger);
    } else if (action === 'dob') {
      event.preventDefault();
      this._saveDob(trigger);
    }
  }

  /**
   * Same-page links scroll rather than jump. The theme sets no scroll-behavior, and setting it on
   * <html> would change every anchor on the storefront, so it is done here — only for links inside
   * this element, which exist only on the loyalty page.
   *
   * Where the target stops is the theme's business, not ours: every loyalty section carries the
   * theme's own .anchor class, whose scroll-margin-top already clears a sticky header, a sticky
   * announcement bar and a 20px gap. scrollIntoView honours that margin; scrollTo would not.
   *
   * Returns false when there is nothing to scroll to, letting the browser handle the link normally.
   */
  _scrollToAnchor(anchor) {
    const id = (anchor.getAttribute('href') || '').slice(1);
    if (!id) return false;

    const target = document.getElementById(id);
    if (!target) return false;

    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start'
    });

    // Keep the URL and focus behaviour a real anchor would have given.
    if (history.replaceState) history.replaceState(null, '', `#${id}`);
    return true;
  }

  /**
   * Stores the customer's birthday against their Appstle profile.
   *
   * POST api/update-customer-birth-date, the same session-authenticated proxy path claim and redeem
   * use — the customer is identified by the signed proxy request, never by anything the page sends.
   * Appstle keeps birthdays to uuuu-MM-dd, which is exactly what a native date input produces, so
   * the value goes straight through.
   *
   * Appstle accepts a birthday once. On success the dialog collapses to a confirmation rather than
   * leaving a control that would only ever be refused a second time.
   */
  async _saveDob(trigger) {
    const modal = trigger.closest('.modal') || this;
    const input = modal.querySelector('[data-loyalty-dob-input]');
    if (!input) return;

    const value = (input.value || '').trim();
    const invalid = trigger.getAttribute('data-invalid-label');

    // Mirrors Appstle's own widget: a real date, not in the future, not before 1900.
    const parsed = value ? new Date(`${value}T00:00:00`) : null;
    const year = parsed ? parsed.getFullYear() : 0;
    if (!parsed || isNaN(parsed.getTime()) || parsed > new Date() || year < 1900) {
      this._errorIn(modal, invalid);
      input.focus();
      return;
    }

    const label = trigger.textContent;
    trigger.disabled = true;
    trigger.dataset.blBusy = '1';
    const busy = trigger.getAttribute('data-busy-label');
    if (busy) trigger.textContent = busy;

    const { ok, payload } = await this._post('api/update-customer-birth-date', {
      dob: value,
      customerId: this.customerId
    });

    delete trigger.dataset.blBusy;

    if (!ok) {
      trigger.disabled = false;
      trigger.textContent = label;
      this._errorIn(modal, payload && payload.message);
      return;
    }

    const done = (trigger.getAttribute('data-done-label') || '').replace('[[date]]', value);
    const field = modal.querySelector('.bl-lwte__dob-field');
    if (field) field.remove();
    trigger.remove();

    const holder = modal.querySelector('.bl-lwte__dob');
    if (holder && done) {
      const note = document.createElement('p');
      note.className = 'bl-lwte__dob-value body-sm';
      note.textContent = done;
      holder.appendChild(note);
    }

    // The card's own button carries the same state, so it stops inviting a second attempt.
    document.querySelectorAll('[aria-controls="' + modal.id + '"] .bl-lwte__follow-label').forEach((node) => {
      const set = node.closest('[aria-controls]').getAttribute('data-set-label');
      if (set) node.textContent = set;
    });
  }

  _copy(trigger) {
    const value = trigger.getAttribute('data-copy') || '';
    const done = () => this._flash(trigger, trigger.getAttribute('data-copied-label') || 'Copied');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(value).then(done, () => {});
    }
  }

  /** Restores the label after a moment, so the button never gets stuck on its feedback text. */
  _flash(trigger, message) {
    if (trigger.dataset.blBusy) return;
    const original = trigger.textContent;
    trigger.dataset.blBusy = '1';
    trigger.textContent = message;
    setTimeout(() => {
      trigger.textContent = original;
      delete trigger.dataset.blBusy;
    }, 2000);
  }

  async _claim(trigger) {
    const earnRuleId = parseInt(trigger.getAttribute('data-earn-rule-id'), 10);
    if (!earnRuleId) return;

    // The follow has to open before the await, or the browser treats it as an unrequested popup.
    const url = trigger.getAttribute('data-visit');
    if (url) window.open(url, '_blank', 'noopener');

    // Already banked: the button is now just the way to the profile. Appstle would answer 400 to a
    // second claim anyway, so the round trip is skipped rather than spent.
    if (trigger.classList.contains('is-claimed')) return;

    trigger.disabled = true;
    const { ok, status } = await this._post('api/claim-social-media-points', { earnRuleId: earnRuleId });

    // 400 is "already claimed", which is the same end state as a fresh claim.
    if (ok || status === 400) {
      const claimed = trigger.getAttribute('data-claimed-label') || 'Followed';
      // The markup owns what "claimed" looks like: the class reveals the tick and the label swaps
      // in place, so a claim made here matches what the server renders on the next load exactly.
      const label = trigger.querySelector('[data-claim-label]');
      if (label) {
        label.textContent = claimed;
      } else {
        trigger.textContent = claimed;
      }
      trigger.classList.add('is-claimed');
      trigger.setAttribute('aria-label', claimed);
      // Stays live — the points are banked but the profile link is still wanted.
      trigger.disabled = false;
    } else {
      trigger.disabled = false;
      this._error(trigger);
    }
  }

  async _redeem(trigger) {
    const redeemRuleId = parseInt(trigger.getAttribute('data-redeem-rule-id'), 10);
    if (!redeemRuleId || !this.customerId) return;

    trigger.disabled = true;
    const label = trigger.textContent;
    trigger.textContent = trigger.getAttribute('data-busy-label') || 'Redeeming…';

    const { ok, payload } = await this._post('api/redeem-points', {
      customerId: parseInt(this.customerId, 10),
      redeemRuleId: redeemRuleId
    });

    if (!ok || !payload) {
      trigger.disabled = false;
      trigger.textContent = label;
      this._error(trigger);
      return;
    }

    // The response is the customer's whole loyalty profile — the same shape as the metafield, with
    // availablePoints and rewards at the top level, so both are read straight off it. Nothing is
    // re-read from the metafield itself: Appstle writes that asynchronously and it would still be
    // showing the pre-redeem balance.
    this._syncBalance(payload.availablePoints);
    this._addReward(payload.rewards);

    // Confirm inside the open dialog, then hand the button back on close. Redeeming is repeatable,
    // so "Redeemed" must not outlive the moment — but it also must not vanish before it is read.
    trigger.dataset.blRedeemed = '1';
    trigger.disabled = true;
    trigger.textContent = trigger.getAttribute('data-redeemed-label') || 'Redeemed';
    this._resetRedeemOnClose(trigger);
  }

  /**
   * Restores a redeemed button once its dialog closes.
   *
   * The theme's OpenableElement backs `open` with a plain attribute, so watching that is enough and
   * needs no theme event. The label is not simply put back: _syncBalance re-tests it against the
   * balance left after the redeem, so a customer who can no longer afford the reward reopens to the
   * shortfall rather than to a button that would fail.
   */
  _resetRedeemOnClose(trigger) {
    const modal = trigger.closest('.modal');
    if (!modal || typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(() => {
      if (modal.hasAttribute('open')) return;
      observer.disconnect();
      delete trigger.dataset.blRedeemed;
      trigger.disabled = false;
      trigger.textContent = trigger.getAttribute('data-redeem-label') || trigger.textContent;
      this._syncBalance(this._points);
    });

    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
  }


  /** Repaints every points figure on the page and re-tests what is now affordable. */
  /** Coerces a number or numeric string, rejecting anything else. Returns null when unusable. */
  _toNumber(value) {
    if (typeof value === 'number') return Number.isNaN(value) ? null : value;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }


  _syncBalance(value) {
    const points = this._toNumber(value);
    if (points === null) return;

    // Kept so a dialog closing later can re-test against the balance as it stands now.
    this._points = points;

    const formatted = points.toLocaleString();
    document.querySelectorAll('[data-loyalty-balance]').forEach((node) => {
      // The count-up is owned by whichever <bl-loyalty> wraps that figure — usually the hero, not
      // the element handling this redeem — so the frame id lives on the node. Cancelling only this
      // element's own would let the hero's animation finish and paint the old total back over the
      // new one.
      if (node._blCountRaf) {
        cancelAnimationFrame(node._blCountRaf);
        node._blCountRaf = null;
      }
      // A count-up that has not been revealed yet still holds the figure captured at page load.
      // Left armed, it would animate to that stale number the moment the hero scrolls into view and
      // undo this update — the balance looked unchanged until a reload.
      node._blCountDone = true;
      node._blPoints = points;
      node.textContent = formatted;
    });

    // Sentences that quote the balance re-render from their own template, so a dialog left open
    // during a redeem cannot keep claiming the old figure.
    document.querySelectorAll('[data-loyalty-balance-line]').forEach((node) => {
      const template = node.getAttribute('data-template');
      if (template) node.textContent = template.replace('[[points]]', formatted);
    });

    document.querySelectorAll('[data-loyalty-action="redeem"]').forEach((button) => {
      const cost = parseInt(button.getAttribute('data-cost'), 10) || 0;
      // Mid-flight, or showing its confirmation until the dialog closes — leave both alone.
      if (button.dataset.blBusy || button.dataset.blRedeemed) return;

      // Affordability moves the disabled state and nothing else. The label is always the offer,
      // which is how the section renders it server-side too: a shortfall used to be written into
      // the button itself, so a reopened dialog accused the customer of being short instead of
      // simply offering the reward again. The balance line above it already states the numbers.
      button.disabled = points < cost;

      const label = button.getAttribute('data-redeem-label');
      if (label) button.textContent = label;
    });
  }

  /** Puts the freshly minted voucher at the top of the wallet without a reload. */
  _addReward(rewards) {
    const list = document.querySelector('[data-loyalty-wallet]');
    const template = document.querySelector('[data-loyalty-reward-template]');
    if (!list || !template || !rewards || !rewards.length) return;

    const reward = rewards[rewards.length - 1];
    const node = template.content.firstElementChild.cloneNode(true);
    const value = node.querySelector('[data-reward-value]');
    const code = node.querySelector('[data-reward-code]');
    const copy = node.querySelector('[data-loyalty-action="copy"]');

    // A voucher has no discount value on it, only the id of the rule that minted it — reading
    // discountValue here is what rendered "RM0.00 off". The redeem button for that rule is already
    // on the page carrying its title, so the name is taken from there and the freshly added card
    // matches the server-rendered ones exactly. description is Appstle's own sentence, used when
    // the rule is not on the page.
    if (value) {
      const source = document.querySelector(
        `[data-loyalty-action="redeem"][data-redeem-rule-id="${reward.pointRedeemRuleId}"]`
      );
      const card = source && source.closest('.modal');
      const title = card && card.querySelector('.heading');
      value.textContent = (title && title.textContent.trim()) || reward.description || '';
    }

    const desc = node.querySelector('[data-reward-desc]');
    if (desc) desc.textContent = reward.description || '';

    const note = node.querySelector('[data-reward-note]');
    if (note) {
      const template = list.getAttribute('data-expiry-template');
      if (template && reward.expireDate) {
        const when = new Date(reward.expireDate);
        note.textContent = template
          .replace('[[date]]', when.toLocaleDateString(undefined, {
            day: 'numeric', month: 'long', year: 'numeric'
          }))
          .replace('[[time]]', when.toLocaleTimeString(undefined, {
            hour: 'numeric', minute: '2-digit'
          }));
      } else {
        note.textContent = '';
      }
    }

    if (code) code.textContent = reward.discountCode || '';
    if (copy) copy.setAttribute('data-copy', reward.discountCode || '');

    const empty = document.querySelector('[data-loyalty-wallet-empty]');
    if (empty) empty.hidden = true;
    list.hidden = false;
    list.prepend(node);
  }

  _error(trigger) {
    this._errorIn(this);
  }

  /**
   * Writes into the nearest error slot rather than the section's. A dialog has its own, and a
   * message shown behind an open modal is a message nobody reads.
   */
  _errorIn(scope, message) {
    const holder = (scope || this).querySelector('[data-loyalty-error]');
    if (!holder) return;
    holder.textContent = message || holder.getAttribute('data-message') || 'Something went wrong. Please try again.';
    holder.hidden = false;
  }
}

if (!window.customElements.get('bl-loyalty')) {
  window.customElements.define('bl-loyalty', BlLoyalty);
}

/**
 * <bl-card-row-fit> — keeps a row of product cards consistent about where the
 * savings pill sits.
 *
 * The pill is inline with the prices by default. Whether it fits is a runtime
 * question — it depends on column width, the price lengths and the loaded font —
 * and CSS cannot ask "did any sibling wrap?", so it is measured here. If any card
 * in a row band cannot fit its pill on the price line, every card in that band is
 * stacked, so a row is never half inline and half stacked.
 *
 * Bands are grouped by offsetTop rather than by index, so it stays correct when
 * the grid rewraps or a promo card spans columns.
 *
 * Wrap the grid, do not replace it: the cards subgrid onto the grid's rows, so the
 * grid has to stay their direct parent.
 */
class BlCardRowFit extends HTMLElement {
  connectedCallback() {
    this._measure = this._measure.bind(this);
    this._lastWidth = -1;

    this._measure();
    window.addEventListener('resize', this._measure);

    // Web fonts land after first paint and change how wide the prices are.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this._measure);

    if (window.ResizeObserver) {
      // Width only: stacking changes the height, and reacting to that would loop.
      this._observer = new ResizeObserver((entries) => {
        const width = Math.round(entries[0].contentRect.width);
        if (width === this._lastWidth) return;
        this._lastWidth = width;
        this._measure();
      });
      this._observer.observe(this);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._measure);
    if (this._observer) this._observer.disconnect();
  }

  _measure() {
    const cards = Array.from(this.querySelectorAll('.bl-product-lineup__card'));
    if (cards.length === 0) return;

    // Measure everything inline first, otherwise a card already stacked always
    // reports as fitting.
    cards.forEach((card) => card.classList.remove('bl-product-lineup__card--stacked-saving'));

    const bands = new Map();
    cards.forEach((card) => {
      const top = Math.round(card.offsetTop);
      if (!bands.has(top)) bands.set(top, []);
      bands.get(top).push(card);
    });

    bands.forEach((band) => {
      const anyWraps = band.some((card) => {
        const row = card.querySelector('.bl-product-lineup__price-row');
        if (!row) return false;

        const items = Array.from(row.children).filter((el) => el.offsetParent !== null);
        if (items.length < 2) return false;

        // Width, not offsetTop: the row is baseline-aligned across three font sizes, so
        // items sitting on the same line still report different tops.
        const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
        const needed = items.reduce((total, el) => total + el.getBoundingClientRect().width, 0) +
          gap * (items.length - 1);

        return needed > row.clientWidth + 0.5;
      });

      if (anyWraps) {
        band.forEach((card) => card.classList.add('bl-product-lineup__card--stacked-saving'));
      }
    });
  }
}

if (!window.customElements.get('bl-card-row-fit')) {
  window.customElements.define('bl-card-row-fit', BlCardRowFit);
}

/* ---------------------------------------------------------------------------
   Mobile menu search pill — back control

   Figma 52:7731 puts the drawer's close X inside the search pill. A back arrow
   replaces it while search results are showing (pure CSS, off `.is-searching`);
   only the back ACTION needs script. A sub-panel's back arrow is in the panel's
   own head and is the theme's `data-action="close"`, delegated on the panel.

   The pill's arrow cannot be `data-action="close"`: that is delegated on the
   nearest OpenableElement, which from the pill is #mobile-menu-drawer, so it
   would close the entire menu. It clears the search instead.
   --------------------------------------------------------------------------- */
class BlMenuSearch extends HTMLElement {
  connectedCallback() {
    this.field = this.querySelector('[data-bl-menu-search-input]');
    this.content = this.closest('.drawer__content');
    this.loading = this.content && this.content.querySelector('.bl-mobile-menu__search-loading');
    this.results = this.content && this.content.querySelector('.bl-mobile-menu__results');

    this.addEventListener('click', (event) => {
      if (!event.target.closest('[data-bl-menu-back]')) return;

      // Otherwise the drawer's own delegated [data-action="close"] listener closes the whole menu.
      event.stopPropagation();

      if (!this.field) return;

      this.field.value = '';
      this.search('');
    });

    if (this.field && this.results) {
      let timer;
      this.field.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(this.field.value.trim()), 200);
      });

      // The injected markup carries the theme's reset button, but its listener lives in
      // PredictiveSearchDrawer -- which this drawer is not. Wire it up here instead.
      this.results.addEventListener('click', (event) => {
        if (!event.target.closest('[data-action="reset-search"]')) return;

        event.preventDefault();
        this.field.value = '';
        this.field.focus();
        this.search('');
      });
    }
  }
  async search(term) {
    if (this.controller) this.controller.abort();

    if (term === '') {
      this.content.classList.remove('is-searching');
      this.loading.hidden = true;
      this.results.hidden = true;
      this.results.innerHTML = '';
      return;
    }

    this.content.classList.add('is-searching');
    this.loading.hidden = false;
    this.results.hidden = true;

    this.controller = new AbortController();

    const routes = (window.themeVariables || {}).routes || {};
    const url = routes.predictiveSearchUrl + '?q=' + encodeURIComponent(term) +
      '&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search';

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      const holder = document.createElement('div');
      holder.innerHTML = await response.text();

      // Same unwrap the theme does: the section renders one wrapper element around the results.
      this.results.innerHTML = holder.firstElementChild.innerHTML;
    } catch (error) {
      if (error.name === 'AbortError') return; // A newer keystroke already replaced this request.
      this.results.innerHTML = '';
    }

    this.loading.hidden = true;
    this.results.hidden = false;
  }
}

if (!window.customElements.get('bl-menu-search')) {
  window.customElements.define('bl-menu-search', BlMenuSearch);
}

/**
 * <a is="bl-scroll-link"> — a same-page link that scrolls rather than jumps.
 *
 * Rendered by snippets/bl_product-rating.liquid when it is given an href: the
 * rating under the product title scrolls down to the reviews section. <html>
 * scrolls smoothly on its own (bl_custom.css, "Smooth scrolling"); this element
 * is for the target lookup and the sticky-header offset below, which a plain
 * anchor cannot do — the same approach the loyalty page takes
 * (BlLoyalty._scrollToAnchor).
 *
 * The target comes from data-bl-scroll-target, a CSS selector list, and is the
 * first match in document order — e.g. the first Judge.me reviews block on the
 * page, found by the block id Shopify renders (shopify-block-judge_me_reviews_…),
 * so the apps section's own id, which differs per template, never matters. The
 * scroll lands on that block's section wrapper. Without a selector, or when
 * nothing matches it, the hash is tried as an element id instead.
 *
 * Section wrappers carry no scroll margin, so the theme's own .anchor offset —
 * sticky header, sticky announcement bar and a 20px gap — is set on the target
 * first, unless it already has one. With no target on the page the click is
 * left to the browser, which does nothing for a missing hash. Reduced motion
 * jumps instead of scrolling.
 */
class BlScrollLink extends HTMLAnchorElement {
  connectedCallback() {
    if (this._wired) return; // re-connected after a move in the DOM
    this._wired = true;
    this.addEventListener('click', this._onClick.bind(this));
  }

  _onClick(event) {
    const target = this._resolveTarget();
    if (!target) return;

    event.preventDefault();

    if (parseFloat(getComputedStyle(target).scrollMarginTop) === 0) {
      target.style.scrollMarginTop = 'calc(var(--header-height, 0px) * var(--enable-sticky-header, 0) + var(--announcement-bar-height, 0px) * var(--enable-sticky-announcement-bar, 0) + 20px)';
    }

    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start'
    });

    if (history.replaceState && target.id) history.replaceState(null, '', '#' + target.id);
  }

  _resolveTarget() {
    const selector = this.getAttribute('data-bl-scroll-target');

    if (selector) {
      let match = null;
      try {
        match = document.querySelector(selector);
      } catch (error) {
        match = null; // a selector the browser cannot parse: fall through to the hash
      }
      if (match) return match.closest('.shopify-section') || match;
    }

    const id = (this.getAttribute('href') || '').replace(/^[^#]*#/, '');
    return id ? document.getElementById(id) : null;
  }
}

if (!window.customElements.get('bl-scroll-link')) {
  window.customElements.define('bl-scroll-link', BlScrollLink, { extends: 'a' });
}

/**
 * <bl-geolocation> — sends visitors from a given country to the store meant
 * for them.
 *
 * Rendered by sections/bl_geolocation.liquid in the header group, hidden,
 * holding one element per Country block with data-countries (ISO codes,
 * comma separated) and data-url. The visitor's country comes from Shopify's
 * browsing-context suggestion endpoint, the one Markets' geolocation
 * recommendations use, read once per page; when it matches a block the
 * visitor is sent to that block's link.
 *
 * Nothing is done for crawlers, for a link back to this host, for a visitor
 * who arrived from the target host (two stores pointing at each other would
 * otherwise bounce them), or within `remember-days` of a redirect, so a
 * visitor can come back to this store on purpose; at 0 every visit redirects.
 * The section renders nothing in the theme editor.
 *
 * Attributes: remember-days.
 */
class BlGeolocation extends HTMLElement {
  connectedCallback() {
    if (this._initialised) return;
    this._initialised = true;

    if (window.Shopify && window.Shopify.designMode) return;

    this.targets = Array.from(this.querySelectorAll('[data-countries]')).map((element) => ({
      countries: element.getAttribute('data-countries').split(',').map((code) => code.trim().toUpperCase()).filter(Boolean),
      url: element.getAttribute('data-url') || ''
    }));

    if (!this.targets.length || this._isCrawler() || this._remembered()) return;

    this._detect().then((country) => {
      if (!country) return;

      const target = this.targets.find((candidate) => candidate.countries.indexOf(country) !== -1);
      if (!target || !target.url) return;

      let url;
      try {
        url = new URL(target.url, window.location.href);
      } catch (error) {
        return;
      }
      if (url.host === window.location.host) return;

      // Arriving from the target store is a choice already made.
      this._remember();
      if (this._referrerHost() === url.host) return;

      window.location.replace(url.toString());
    }).catch(() => {
      // The lookup failed: the visitor stays.
    });
  }

  async _detect() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(root + 'browsing_context_suggestions.json?country[enabled]=true', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) return null;

    const json = await response.json();
    const country = json && json.detected_values && json.detected_values.country;
    return country && country.handle ? String(country.handle).toUpperCase() : null;
  }

  _referrerHost() {
    try {
      return document.referrer ? new URL(document.referrer).host : '';
    } catch (error) {
      return '';
    }
  }

  _isCrawler() {
    return /bot|crawl|spider|slurp|facebookexternalhit|lighthouse|headless|preview/i.test(navigator.userAgent);
  }

  get _storageKey() {
    return 'bl-geolocation-redirected-until';
  }

  get _rememberDays() {
    const days = parseInt(this.getAttribute('remember-days'), 10);
    return Number.isNaN(days) ? 30 : Math.max(0, days);
  }

  _remembered() {
    if (this._rememberDays === 0) return false;

    try {
      return parseInt(window.localStorage.getItem(this._storageKey), 10) > Date.now();
    } catch (error) {
      return false;
    }
  }

  _remember() {
    const days = this._rememberDays;
    if (days === 0) return;

    try {
      window.localStorage.setItem(this._storageKey, String(Date.now() + days * 86400000));
    } catch (error) {
      // Storage unavailable: the referrer check still stops a loop.
    }
  }
}

if (!window.customElements.get('bl-geolocation')) {
  window.customElements.define('bl-geolocation', BlGeolocation);
}


/**
 * <bl-cart-tiers> — keeps the tiering discount's gifts in step with the cart.
 *
 * Rendered by snippets/bl_cart-tiers.liquid inside the drawer, holding one
 * hidden element per gift tier with data-tier, data-variant and
 * data-threshold (cents). The cart value is the total after discounts minus
 * the gift lines themselves, which carry the hidden _bl_tier_gift property
 * set to their tier's number. A tier whose value is reached and whose gift is
 * not in the cart gets it added; a gift whose tier is no longer reached, no
 * longer a gift tier, or no longer that product is removed. Removals go in one
 * update, additions in one add, each bringing the drawer's HTML back with it,
 * then the cart is read back and the drawer refreshed the way the theme does.
 *
 * The drawer shows the work while it runs. Liquid renders a gift about to go
 * with the theme's line loader showing and a gift still to be added as a
 * placeholder line at the top (bl_cart-tiers, parts 'removals' and
 * 'placeholders'), straight from the cart it rendered, so a redraw in between
 * keeps them. When the removal has gone through, the theme's own end event
 * fades the spinner and collapses the line ahead of the redraw; the redraw
 * then swaps the placeholder for the real gift line.
 *
 * Runs on every cart:updated on the document — the theme's product form, line
 * quantities and the BL elements all dispatch it — and once on connect when
 * Liquid saw a mismatch (needs-sync), reading the cart first. The drawer
 * re-renders while a run is in flight and connects a new instance, so the lock
 * is shared by every instance and the plan is confirmed against a fresh read
 * before anything is sent; otherwise two instances could add the same gift
 * twice. Its own refresh dispatches cart:updated again, which finds nothing
 * left to do. A gift line is kept at one unit. Never runs in the theme editor.
 *
 * Attributes: update-url, needs-sync.
 */
let blCartTiersBusy = false;

class BlCartTiers extends HTMLElement {
  connectedCallback() {
    if (window.Shopify && window.Shopify.designMode) return;

    this.tiers = Array.from(this.querySelectorAll('[data-tier]')).map((element) => ({
      tier: element.getAttribute('data-tier'),
      variantId: element.getAttribute('data-variant') || '',
      threshold: parseInt(element.getAttribute('data-threshold'), 10) || 0
    }));

    this._onCartUpdated = this._onCartUpdated || ((event) => this._reconcile(event.detail && event.detail.cart));
    document.documentElement.addEventListener('cart:updated', this._onCartUpdated);

    if (this.hasAttribute('needs-sync')) {
      this._fetchCart().then((cart) => this._reconcile(cart)).catch(() => {});
    }
  }

  disconnectedCallback() {
    document.documentElement.removeEventListener('cart:updated', this._onCartUpdated);
  }

  _fetchCart() {
    return fetch(window.themeVariables.routes.cartUrl + '.js', { cache: 'reload' }).then((response) => response.json());
  }

  async _post(url, body) {
    const response = await fetch(url + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('cart request failed');
    return response.json();
  }

  /**
   * The cart value the tiers measure: the total after discounts, without the
   * gift lines, so a gift that is not free yet cannot unlock its own tier.
   */
  _value(cart) {
    let value = cart.total_price;
    cart.items.forEach((item) => {
      if (item.properties && item.properties._bl_tier_gift) value -= item.final_line_price;
    });
    return value;
  }

  /**
   * What `cart` needs: gift lines to drop or put back to one unit (updates,
   * by key) and gifts to add.
   */
  _plan(cart) {
    const value = this._value(cart);
    const tiers = new Map(this.tiers.map((tier) => [tier.tier, tier]));
    const present = new Set();
    const updates = {};
    const adds = [];

    cart.items.forEach((item) => {
      const tag = item.properties && item.properties._bl_tier_gift;
      if (!tag) return;

      const tier = tiers.get(String(tag));
      if (tier && String(item.variant_id) === tier.variantId && value >= tier.threshold && !present.has(tier.tier)) {
        present.add(tier.tier);
        if (item.quantity !== 1) updates[item.key] = 1;
      } else {
        updates[item.key] = 0;
      }
    });

    this.tiers.forEach((tier) => {
      if (tier.variantId && value >= tier.threshold && !present.has(tier.tier)) {
        adds.push({ id: parseInt(tier.variantId, 10), quantity: 1, properties: { _bl_tier_gift: tier.tier } });
      }
    });

    return { adds: adds, updates: updates };
  }

  _hasWork(plan) {
    return plan.adds.length > 0 || Object.keys(plan.updates).length > 0;
  }

  async _reconcile(cart) {
    if (blCartTiersBusy || !cart || !Array.isArray(cart.items)) return;
    if (!this._hasWork(this._plan(cart))) return;

    blCartTiersBusy = true;

    let removedKeys = [];

    try {
      // The cart handed in may be a step behind; act only on what a fresh read still needs.
      const plan = this._plan(await this._fetchCart());
      if (!this._hasWork(plan)) return;

      removedKeys = Object.keys(plan.updates).filter((key) => plan.updates[key] === 0);
      this._showRemoving(removedKeys);

      // Each request brings the drawer's HTML back with it, so the redraw needs no extra read.
      let last = null;
      if (Object.keys(plan.updates).length) last = await this._post(this.getAttribute('update-url'), { updates: plan.updates, sections: ['mini-cart'] });
      if (plan.adds.length) last = await this._post(window.themeVariables.routes.cartAddUrl, { items: plan.adds, sections: ['mini-cart'] });

      const fresh = await this._fetchCart();
      this._finishRemoving(removedKeys, fresh);
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: fresh } }));

      const sections = last && last.sections ? last.sections : null;
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: sections ? { cart: Object.assign({}, fresh, { sections: sections }), replacementDelay: removedKeys.length ? 400 : 0 } : undefined
      }));
    } catch (error) {
      // A gift that cannot be added (sold out) or a dropped request: left for the next cart change.
      this._settlePending(removedKeys);
    } finally {
      blCartTiersBusy = false;
    }
  }

  _lines(keys) {
    const drawer = document.getElementById('mini-cart');
    if (!drawer) return [];
    return keys.map((key) => drawer.querySelector('line-item[data-bl-key="' + key + '"]')).filter(Boolean);
  }

  /**
   * The drawer renders a gift that is about to go with its loader showing
   * (mini-cart.liquid, from the 'removals' part of bl_cart-tiers). Should it
   * be showing an older state, the theme's own start event puts it there.
   */
  _showRemoving(keys) {
    this._lines(keys).forEach((line) => {
      line.dispatchEvent(new CustomEvent('line-item-quantity:change:start', { bubbles: true, detail: { newLineQuantity: 0 } }));
    });
  }

  /**
   * The theme's end event: <line-item> fades its spinner and collapses, the
   * way a line removed by hand does, ahead of the redraw.
   */
  _finishRemoving(keys, cart) {
    this._lines(keys).forEach((line) => {
      line.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true, detail: { cart: cart, newLineQuantity: 0 } }));
    });
  }

  /**
   * Nothing changed: the spinners stop and the placeholders go, until the next
   * cart change renders them again if the cart still disagrees.
   */
  _settlePending(keys) {
    this._lines(keys).forEach((line) => {
      const loader = line.querySelector('.line-item__loader');
      if (loader) loader.hidden = true;
      line.classList.remove('bl-cart-tiers__pending');
      line.removeAttribute('aria-busy');
    });

    const drawer = document.getElementById('mini-cart');
    if (drawer) drawer.querySelectorAll('line-item[data-bl-tier-pending]').forEach((line) => line.remove());
  }
}

if (!window.customElements.get('bl-cart-tiers')) {
  window.customElements.define('bl-cart-tiers', BlCartTiers);
}


/**
 * <bl-cart-quick-add> — the cart drawer's quick-add sheet.
 *
 * Rendered by sections/mini-cart.liquid as a direct child of the drawer,
 * empty: an overlay, a close button and a content box. A tap on any
 * [data-bl-quick-add] button (the recommendation cards' Add, carrying the
 * product URL) fetches that product's page, lifts the #bl-quick-add-content
 * template out of it (snippets/bl_product-quick-add.liquid) and opens the
 * sheet with it: image, title, price, the variant picker and packs, and the
 * add to cart button, all the product page's own markup. A product page
 * without the template, one on another section, sends the visitor there
 * instead.
 *
 * Built on the theme's <openable-element>, so open, the overlay, the close
 * button and the focus trap are the theme's. The theme's product form inside
 * does the adding and dispatches variant:added, which closes the sheet; the
 * form's own cart:refresh then re-renders the drawer. The content is dropped
 * once the sheet has closed, so the next tap starts clean.
 */
const BlOpenableElement = window.customElements.get('openable-element');

if (BlOpenableElement && !window.customElements.get('bl-cart-quick-add')) {
  class BlCartQuickAdd extends BlOpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this._onDocumentClick = this._onDocumentClick || this._handleDocumentClick.bind(this);
      document.addEventListener('click', this._onDocumentClick);

      this.addEventListener('variant:added', () => {
        this.open = false;
      });
      this.addEventListener('openable-element:close', () => {
        this.addEventListener('transitionend', (event) => {
          if (event.target === this) this._clear();
        }, { once: true });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick);
    }

    _handleDocumentClick(event) {
      const button = event.target.closest('[data-bl-quick-add]');
      if (!button) return;

      event.preventDefault();
      this._trigger = button;
      this.setAttribute('href', button.getAttribute('data-bl-quick-add'));
      this.open = true;
    }

    get requiresLoading() {
      return this.hasAttribute('href');
    }

    async _load() {
      const href = this.getAttribute('href');
      if (!href) return;

      const button = this._trigger;
      if (button) button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(href, { headers: { Accept: 'text/html' } });
        const fragment = document.createRange().createContextualFragment(await response.text());
        const template = fragment.querySelector('#bl-quick-add-content');

        if (!template) {
          window.location.href = href;
          return;
        }

        const content = this.querySelector('[data-bl-quick-add-content]');
        const lifted = document.importNode(template.content, true);
        // The tags go in first and outside the product-rerender wrapper, so they survive a variant swap and
        // exist before <bl-variant-packs> writes its lines
        content.replaceChildren(...this._tagInputs(button, lifted), lifted);
        content.scrollTop = 0;
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    }

    /**
     * Add-on analytics: hidden _ns_src / _ns_anchor / _ns_slot inputs for the lifted product form, taken from the
     * recommendation card that opened the sheet. They join the form through its id, so they follow it into
     * FormData. Empty when the sheet was opened from anywhere but a recommendation card.
     */
    _tagInputs(button, lifted) {
      const recommendations = button && button.closest('bl-cart-recommendations');
      const card = button && button.closest('.bl-cart-rec');
      const form = lifted.querySelector('form[is="product-form"]') || lifted.querySelector('form[id]');
      const formId = form && form.getAttribute('id');
      if (!recommendations || !card || !formId) return [];

      // Cards of products already in the cart are removed from the list, so the live position is the slot
      const siblings = card.parentElement ? Array.from(card.parentElement.children) : [];
      const slot = siblings.length ? siblings.indexOf(card) + 1 : card.getAttribute('data-ns-slot');

      return [
        ['_ns_src', 'cart_quickadd'],
        ['_ns_anchor', recommendations.getAttribute('data-anchor-handle') || ''],
        ['_ns_slot', String(slot || '')]
      ].map(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + key + ']';
        input.value = value;
        input.setAttribute('form', formId);
        input.setAttribute('data-ns-prop', key);
        if (key === '_ns_src') input.setAttribute('data-ns-item', button.getAttribute('data-ns-item') || '');
        return input;
      });
    }

    _clear() {
      const content = this.querySelector('[data-bl-quick-add-content]');
      if (content) content.replaceChildren();
      this.removeAttribute('href');
    }
  }

  window.customElements.define('bl-cart-quick-add', BlCartQuickAdd);
}


/**
 * <bl-warranty-prepair> — pairs the extended warranty inside the add itself.
 *
 * Rendered by snippets/bl_warranty-prepair.liquid inside a product form, with
 * four disabled hidden inputs: the product's WarrantyID property, and the
 * warranty as a second item (items[90]) with its quantity and the same ID.
 * On submit, before the theme's form posts, they are filled in and enabled:
 * a fresh five-digit ID, the warranty quantity matched to the product's. The
 * theme's one request then adds both lines paired, so the drawer renders the
 * toggle on with nothing left to do. When the customer has turned the
 * warranty off for this variant and it is still in the cart (the drawer's own
 * memory), the inputs stay disabled and the product is added alone, as before.
 */
class BlWarrantyPrepair extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    if (!this.form || this._bound) return;
    this._bound = true;

    this._onSubmit = this._prepare.bind(this);
    this.form.addEventListener('submit', this._onSubmit, true);
  }

  disconnectedCallback() {
    if (this.form && this._onSubmit) this.form.removeEventListener('submit', this._onSubmit, true);
    this._bound = false;
  }

  _prepare() {
    const form = this.form;
    const inputs = Array.from(this.querySelectorAll('input'));
    const variantInput = form.querySelector('input[name="id"]:not([disabled])') || form.querySelector('input[name="id"]');
    const variantId = variantInput ? variantInput.value : '';

    let declined = false;
    try {
      declined = Boolean(window.sessionStorage.getItem('bl-warranty-declined:' + variantId));
    } catch (error) {
      declined = false;
    }

    if (declined || !variantId) {
      inputs.forEach((input) => { input.disabled = true; });
      return;
    }

    const formId = form.getAttribute('id');
    const quantityInput = form.querySelector('[name="quantity"]') || (formId && document.querySelector('[name="quantity"][form="' + formId + '"]'));
    const quantity = Math.max(1, parseInt(quantityInput ? quantityInput.value : '1', 10) || 1);
    const warrantyId = String(Math.floor(10000 + Math.random() * 90000));

    this.querySelector('[data-bl-prepair-tag]').value = warrantyId;
    this.querySelector('[data-bl-prepair-item-tag]').value = warrantyId;
    this.querySelector('[data-bl-prepair-quantity]').value = String(quantity);
    inputs.forEach((input) => { input.disabled = false; });
  }
}

if (!window.customElements.get('bl-warranty-prepair')) {
  window.customElements.define('bl-warranty-prepair', BlWarrantyPrepair);
}

/**
 * <bl-checkout-activity> — the "N people are checking out now" pill in the cart drawer footer.
 *
 * The number is not a live count. It is worked out here from the hour of the day in Malaysia and
 * Singapore time (both UTC+8, so one fixed offset serves both stores), between data-min and data-max:
 * a traffic-shaped curve (quiet overnight, peak in the evening) plus a small seeded jitter. The seed is
 * the date and the hour, so the number holds still for the whole hour and is the same for every visitor.
 *
 * The pill ships hidden and is shown once the number is in. The drawer re-renders its HTML on every
 * cart change, which connects a fresh element each time, so connectedCallback is the only hook needed.
 */
const BL_CHECKOUT_ACTIVITY_CURVE = [
  0.12, 0.06, 0.02, 0.00, 0.00, 0.04, 0.12, 0.26,
  0.40, 0.52, 0.60, 0.64, 0.68, 0.64, 0.62, 0.64,
  0.68, 0.74, 0.82, 0.92, 1.00, 0.98, 0.78, 0.42
];

class BlCheckoutActivity extends HTMLElement {
  connectedCallback() {
    const count = this._count();
    const target = this.querySelector('[data-bl-checkout-count]');

    if (!target || count === null) return;

    target.textContent = String(count);
    this.hidden = false;
  }

  _count() {
    let min = parseInt(this.dataset.min, 10);
    let max = parseInt(this.dataset.max, 10);

    if (isNaN(min) || isNaN(max)) return null;
    if (min > max) { const swap = min; min = max; max = swap; }

    const now = new Date(Date.now() + 8 * 60 * 60 * 1000);
    const hour = now.getUTCHours();
    const day = now.getUTCFullYear() * 10000 + (now.getUTCMonth() + 1) * 100 + now.getUTCDate();

    let seed = (day * 31 + hour * 7919) >>> 0;
    seed = Math.imul(seed ^ (seed >>> 15), 2246822519) >>> 0;
    seed = Math.imul(seed ^ (seed >>> 13), 3266489917) >>> 0;
    seed = (seed ^ (seed >>> 16)) >>> 0;

    const jitter = ((seed % 1000) / 1000 - 0.5) * 0.16;
    const value = min + (max - min) * (BL_CHECKOUT_ACTIVITY_CURVE[hour] + jitter);

    return Math.round(Math.min(max, Math.max(min, value)));
  }
}

if (!window.customElements.get('bl-checkout-activity')) {
  window.customElements.define('bl-checkout-activity', BlCheckoutActivity);
}

/**
 * Cart count. Keeps every <cart-count> at the number of items without the hidden warranty lines, the same figure as
 * "Subtotal (N items)" in the cart drawer, and adds .is-bl-pulsing while that number is above zero so the header can
 * pulse a ring behind the bubble (sections/header.liquid, "Pulse the cart count"). The theme rewrites the bubble with
 * the cart's raw item_count on every change, so each bubble gets its own observer: when the text is not the figure
 * this script set, it puts the right number back at once (the raw count less the known warranty units, before
 * the next paint, so the raw figure never shows) and then confirms it from /cart.js. The warranty product id comes from the drawer
 * (data-bl-warranty-product-id). New bubbles (a re-rendered header) are picked up on the next cart event or section
 * load. The pulse class only matters when the header setting is on, so it is harmless when it is off.
 */
(function () {
  const observed = new WeakSet();

  const warrantyId = () => {
    const drawer = document.querySelector('cart-drawer');
    return drawer ? drawer.getAttribute('data-bl-warranty-product-id') || '' : '';
  };

  /* The warranty units in the cart as last known: from the drawer's HTML, then from every /cart.js read below. */
  let warrantyQty = null;

  const knownWarrantyQty = () => {
    if (warrantyQty !== null) return warrantyQty;
    const drawer = document.querySelector('cart-drawer');
    const fromDrawer = drawer ? parseInt(drawer.getAttribute('data-bl-warranty-qty'), 10) : NaN;
    return isNaN(fromDrawer) ? 0 : fromDrawer;
  };

  const sync = (bubble) => {
    const count = parseInt(bubble.textContent, 10) || 0;
    bubble.classList.toggle('is-bl-pulsing', count > 0);
  };

  const correct = async (bubble) => {
    const id = warrantyId();
    if (!id) return;

    try {
      const cart = await fetch(window.Shopify.routes.root + 'cart.js').then((response) => response.json());
      const count = cart.items.reduce((total, line) => (String(line.product_id) === id ? total : total + line.quantity), 0);

      warrantyQty = cart.items.reduce((total, line) => (String(line.product_id) === id ? total + line.quantity : total), 0);
      bubble._blCount = String(count);
      if (bubble.textContent.trim() !== bubble._blCount) bubble.textContent = bubble._blCount;
      sync(bubble);
    } catch (error) {
      /* leave the theme's own number in place */
    }
  };

  const scan = () => {
    document.querySelectorAll('cart-count').forEach((bubble) => {
      if (observed.has(bubble)) return;
      observed.add(bubble);

      new MutationObserver(() => {
        if (bubble.textContent.trim() !== bubble._blCount) {
          /* The theme just wrote the raw count. Observer callbacks run before the next paint, so put back
             the best guess right now (raw less the warranty units already known) and confirm it from the cart. */
          const raw = parseInt(bubble.textContent, 10);

          if (!isNaN(raw) && warrantyId()) {
            bubble._blCount = String(Math.max(0, raw - knownWarrantyQty()));
            bubble.textContent = bubble._blCount;
          }

          correct(bubble);
        }

        sync(bubble);
      }).observe(bubble, { childList: true, characterData: true, subtree: true });

      bubble._blCount = bubble.textContent.trim();
      sync(bubble);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  ['cart:updated', 'cart:refresh', 'shopify:section:load'].forEach((name) => {
    document.addEventListener(name, scan);
  });
})();
